import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaArrowLeft,
  FaBook,
  FaBookmark,
  FaBug,
  FaCheck,
  FaTimes,
  FaChevronDown,
  FaChevronLeft,
  FaClipboardCheck,
  FaClipboardList,
  FaCog,
  FaCode,
  FaDatabase,
  FaLink,
  FaStickyNote,
  FaDownload,
  FaGraduationCap,
  FaCommentDots,
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useClickOutside } from '../hooks/useClickOutside.ts';
import { getAvatarSrc } from '../utils/avatar.ts';
import { useAchievementNotifications } from '../contexts/AchievementNotificationContext.tsx';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus.ts';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { PremiumRequiredModal } from '../components/PremiumRequiredModal.tsx';
import { API_BASE, EXECUTOR_BASE, AI_BASE } from '../utils/apiEndpoints';
import { parseJsonResponse } from '../utils/httpJson.ts';
import { problemPath } from '../utils/problemUrls.ts';
import { authHref, currentReturnPath } from '../utils/authRedirect.ts';
import { topicSeoLabelRu } from '../utils/seoTopics.ts';
import { publicTestIndexOrZero, isPublicTestCaseIndex } from '../utils/visibleTestCases.ts';
import { useSeoMeta, defaultSiteDescription } from '../hooks/useSeoMeta.ts';
import {
  isProblemBookmarked,
  migrateLegacyBookmarkIfNeeded,
  readProblemBookmarks,
  removeProblemBookmark,
  toggleProblemBookmark,
  type ProblemBookmark,
} from '../utils/problemBookmarks.ts';
import { SqlSchemaDiagram } from '../components/SqlSchemaDiagram.tsx';
import {
  ProblemBottomPanel,
  type BottomPanelTab,
  type SubmissionPanelState,
} from '../components/ProblemBottomPanel.tsx';
import { SubmissionHistoryList } from '../components/SubmissionHistoryList.tsx';
import { SubmissionCodeModal } from '../components/SubmissionCodeModal.tsx';
import { isTabularRunValue, renderPandasRunValue } from '../components/PandasResultView.tsx';
import { pickSqlForTestCase, sqlTestCaseInputView } from '../utils/sqlSeedTables.ts';
import { EditorSettingsModal } from '../components/EditorSettingsModal.tsx';
import {
  defineEditorThemes,
  readStoredCodeCustomThemeTokens,
  readStoredCodeSyntaxTheme,
  readStoredSqlCustomThemeTokens,
  readStoredSqlSyntaxTheme,
  readStoredCodeEditorBackgroundMode,
  readStoredSqlEditorBackgroundMode,
  resolveEditorTheme,
  writeStoredCodeCustomThemeTokens,
  writeStoredCodeSyntaxTheme,
  writeStoredSqlCustomThemeTokens,
  writeStoredSqlSyntaxTheme,
  writeStoredCodeEditorBackgroundMode,
  writeStoredSqlEditorBackgroundMode,
  type EditorBackgroundMode,
  type SciplexCodeThemeId,
  type SciplexSqlThemeId,
  type CodeCustomThemeTokens,
  type SqlCustomThemeTokens,
} from '../utils/monacoThemes.ts';
import {
  fetchEditorSettings,
  saveEditorSettings,
} from '../utils/sqlEditorSettingsApi.ts';
import {
  buildSubmissionErrorMessage,
  normalizeSubmissionStatus,
} from '../utils/submissionStatus.ts';
import './css/ProblemPage.css';

/** Ключ черновика в localStorage: переживает перезагрузку вкладки. */
function hasExampleInput(input: unknown): boolean {
  if (input == null) return false;
  if (typeof input === 'string') return input.trim() !== '';
  if (Array.isArray(input)) return input.length > 0;
  if (typeof input === 'object') return Object.keys(input).length > 0;
  return true;
}

function problemDraftStorageKey(
  problemId: number,
  language: string,
  sqlDialect: 'postgres' | 'mysql',
  sandboxPresetKey?: string
): string {
  if (language === 'sql') {
    if (sandboxPresetKey) {
      return `nn_problem_draft_${problemId}_sql_${sqlDialect}_preset_${sandboxPresetKey}`;
    }
    return `nn_problem_draft_${problemId}_sql_${sqlDialect}`;
  }
  return `nn_problem_draft_${problemId}_${language}`;
}

const SANDBOX_PRESET_STORAGE_PREFIX = 'nn_sql_sandbox_preset_';

function readSandboxPresetChoice(problemId: number, presets: SqlSandboxPreset[]): string {
  try {
    const saved = localStorage.getItem(`${SANDBOX_PRESET_STORAGE_PREFIX}${problemId}`);
    if (saved && presets.some((preset) => preset.key === saved)) {
      return saved;
    }
  } catch {
    // ignore
  }
  return presets[0]?.key || 'family';
}

function writeSandboxPresetChoice(problemId: number, presetKey: string): void {
  try {
    localStorage.setItem(`${SANDBOX_PRESET_STORAGE_PREFIX}${problemId}`, presetKey);
  } catch {
    // ignore
  }
}

function getSandboxPresetLabel(
  preset: SqlSandboxPreset,
  language: string | undefined
): string {
  const lang = language || 'ru';
  return preset.label_i18n?.[lang] || preset.label || preset.key;
}

function getSandboxPresetDescription(
  preset: SqlSandboxPreset,
  language: string | undefined
): string {
  const lang = language || 'ru';
  return preset.description_i18n?.[lang] || preset.description || '';
}

function getSandboxStarterCode(
  preset: SqlSandboxPreset,
  sqlDialect: 'postgres' | 'mysql'
): string {
  if (sqlDialect === 'mysql') {
    return preset.starter_sql_mysql?.trim() || preset.starter_sql?.trim() || '';
  }
  return preset.starter_sql?.trim() || preset.starter_sql_mysql?.trim() || '';
}

function getSandboxSchemaSql(
  preset: SqlSandboxPreset,
  sqlDialect: 'postgres' | 'mysql'
): string {
  if (sqlDialect === 'mysql') {
    return preset.schema_sql_mysql?.trim() || preset.schema_sql?.trim() || '';
  }
  return preset.schema_sql?.trim() || preset.schema_sql_mysql?.trim() || '';
}

const DESC_PANE_DEFAULT_WIDTH = 360;
const DESC_PANE_MIN_WIDTH = 240;
const DESC_PANE_MAX_WIDTH_RATIO = 0.55;

function clampDescPaneWidth(width: number): number {
  const maxWidth = Math.max(
    DESC_PANE_MIN_WIDTH,
    Math.round(window.innerWidth * DESC_PANE_MAX_WIDTH_RATIO)
  );
  return Math.min(maxWidth, Math.max(DESC_PANE_MIN_WIDTH, Math.round(width)));
}

function readProblemDraft(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeProblemDraft(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // квота или приватный режим
  }
}

function clearProblemDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function resolveEditorCodeFromStorage(
  problem: Pick<Problem, 'id'>,
  language: string,
  sqlDialect: 'postgres' | 'mysql',
  starterCode: string,
  sandboxPresetKey?: string
): string {
  const key = problemDraftStorageKey(problem.id, language, sqlDialect, sandboxPresetKey);
  const saved = readProblemDraft(key);
  return saved !== null ? saved : starterCode;
}

interface SqlSandboxPreset {
  key: string;
  label: string;
  label_i18n?: Record<string, string>;
  description?: string;
  description_i18n?: Record<string, string>;
  schema_sql?: string;
  schema_sql_mysql?: string;
  starter_sql?: string;
  starter_sql_mysql?: string;
}

interface Problem {
  id: number;
  title: string;
  slug?: string | null;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  description: string;
  topic?: string;
  topic_slug?: string;
  canonical_path?: string;
  topic_slug_mismatch?: boolean;
  status?: 'Solved' | 'Attempted' | 'Unsolved';
  examples: {
    input?: string | Record<string, unknown>;
    output: string;
    explanation?: string;
  }[];
  constraints: string[];
  tags: string[];
  premium: boolean;
  starter_code: {
    python?: string;
    javascript?: string;
    java?: string;
    cpp?: string;
    c?: string;
    csharp?: string;
    sql?: string;
  };
  solution_code?: {
    python?: string;
    javascript?: string;
    sql?: string;
    sql_mysql?: string;
    answer?: string;
  };
  test_cases?: Array<{
    schema_sql?: string;
    schema_sql_mysql?: string;
    seed_sql?: string;
    seed_sql_mysql?: string;
    expected?: unknown;
    input?: Record<string, unknown>;
    output?: unknown;
    hidden?: boolean;
  }>;
  sql_read_only?: boolean;
  is_sql_sandbox?: boolean;
  sql_sandbox_presets?: SqlSandboxPreset[];
  is_interactive?: boolean;
  interactive_config?: {
    type?: string;
    image_url?: string;
    download_filename?: string;
  };
  admin_only?: boolean;
}

interface LinkedTheoryLesson {
  id: number;
  slug: string;
  title: string;
  category: {
    slug: string;
    title: string;
  } | null;
}

interface TestResult {
  test_case: number;
  input: string;
  expected: string;
  actual: string;
  passed: boolean;
}

interface SubmissionResponse {
  status: 'Accepted' | 'Wrong Answer' | 'Error';
  message?: string;
  results?: TestResult[];
  error?: string;
  details?: string;
  test_case?: number;
  execution_time?: number;
  memory_usage?: number;
  passed_tests?: number;
  total_tests?: number;
  passed?: number;
  expected?: unknown;
  actual?: unknown;
}

function renderSqlRunResult(
  data: unknown,
  emptyMessage: string,
  columnOrder?: string[]
): React.ReactNode {
  if (data == null) {
    return <div className="sql-result-empty">{emptyMessage}</div>;
  }
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="sql-result-wrapper">
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </div>
    );
  }
  const first = data[0];
  if (typeof first !== 'object' || first === null || Array.isArray(first)) {
    return (
      <div className="sql-result-wrapper">
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </div>
    );
  }
  const rowKeys = Object.keys(first as Record<string, unknown>);
  const columns = columnOrder?.length
    ? columnOrder.filter((col) => rowKeys.includes(col))
    : rowKeys;
  const extraColumns = rowKeys.filter((col) => !columns.includes(col));
  const orderedColumns = [...columns, ...extraColumns];
  return (
    <div className="sql-result-wrapper">
      <table className="sql-result-table">
        <thead>
          <tr>
            {orderedColumns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              {orderedColumns.map((col) => (
                <td key={col}>
                  {(row as Record<string, unknown>)[col] == null
                    ? 'NULL'
                    : String((row as Record<string, unknown>)[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sql-result-meta">
        {data.length} row{data.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}


function buildSubmissionPanelState(
  responseData: SubmissionResponse,
  testCaseCount: number,
  fallbackExecutionMs?: number
): SubmissionPanelState {
  const total =
    responseData.total_tests ??
    (testCaseCount > 0 ? testCaseCount : undefined) ??
    (responseData.status === 'Accepted' && responseData.passed != null
      ? responseData.passed
      : testCaseCount);
  let passed =
    responseData.passed_tests ??
    (responseData.status === 'Accepted'
      ? (responseData.passed ?? total)
      : responseData.passed ?? 0);
  if (
    responseData.status !== 'Accepted' &&
    responseData.test_case != null &&
    responseData.passed_tests == null &&
    responseData.passed == null
  ) {
    passed = Math.max(0, responseData.test_case - 1);
  }
  const status = responseData.status || 'Error';
  const hideExtraMessage = status === 'Wrong Answer' || status === 'Accepted';

  const failedIsPublic =
    responseData.test_case == null || isPublicTestCaseIndex(responseData.test_case - 1);

  return {
    status,
    message: hideExtraMessage ? undefined : responseData.message,
    error: hideExtraMessage ? undefined : responseData.error,
    details: hideExtraMessage ? undefined : responseData.details,
    failedTestCase: responseData.test_case,
    expected: failedIsPublic ? responseData.expected : undefined,
    actual: failedIsPublic ? responseData.actual : undefined,
    passedTests: passed,
    totalTests: total || testCaseCount,
    executionTime:
      responseData.execution_time ??
      (fallbackExecutionMs != null ? fallbackExecutionMs : undefined),
    memoryUsage: responseData.memory_usage,
  };
}

function parseExampleIoValue(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  const trimmed = raw.trim();
  if (!trimmed || !(trimmed.startsWith('{') || trimmed.startsWith('['))) return raw;
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
}

/** Если в примере проза, а в открытом тесте уже есть таблица — показываем её. */
function resolveExampleInput(
  exampleInput: unknown,
  testInput: unknown,
  testCase?: {
    schema_sql?: string;
    schema_sql_mysql?: string;
    seed_sql?: string;
    seed_sql_mysql?: string;
  },
  sqlDialect: 'postgres' | 'mysql' = 'postgres'
): unknown {
  const parsed = parseExampleIoValue(exampleInput);
  if (isTabularRunValue(parsed)) return parsed;
  if (isTabularRunValue(testInput)) return testInput;
  const sql = pickSqlForTestCase(testCase, sqlDialect);
  const sqlView = sqlTestCaseInputView(sql.schema, sql.seed);
  if (sqlView.kind === 'tables') return sqlView.payload;
  if (sqlView.kind === 'sql') return sqlView.text;
  return exampleInput;
}

function renderExampleIo(raw: unknown): React.ReactNode {
  const value = parseExampleIoValue(raw);
  if (isTabularRunValue(value)) {
    return <div className="example-io-tables">{renderPandasRunValue(value)}</div>;
  }
  if (typeof raw === 'string') return <code>{raw}</code>;
  if (raw == null) return null;
  return <code>{JSON.stringify(raw)}</code>;
}

/** Вывод «Запустить» для кода (не SQL): DataFrame/Series — таблица, иначе компактный JSON. */
function renderCodeRunResult(output: string, emptyMessage: string): React.ReactNode {
  const raw = (output || '').trim();
  if (!raw) {
    return <div className="sql-result-empty">{emptyMessage}</div>;
  }
  try {
    const data = JSON.parse(raw);
    if (isTabularRunValue(data)) {
      return renderPandasRunValue(data);
    }
    return <pre className="code-run-output">{JSON.stringify(data)}</pre>;
  } catch {
    return <pre className="code-run-output">{output}</pre>;
  }
}

function getSolutionCodeFromProblem(
  problem: Pick<Problem, 'topic' | 'solution_code'>,
  language: string,
  sqlDialect: 'postgres' | 'mysql'
): string | null {
  const sc = problem.solution_code;
  if (!sc) return null;
  if (problem.topic === 'SQL') {
    if (sqlDialect === 'mysql') {
      return sc.sql_mysql?.trim() || sc.sql?.trim() || null;
    }
    return sc.sql?.trim() || sc.sql_mysql?.trim() || null;
  }
  if (language === 'javascript') {
    return sc.javascript?.trim() || null;
  }
  if (language === 'python') {
    return sc.python?.trim() || null;
  }
  return null;
}

function getSolutionFileName(lang: string): string {
  const map: Record<string, string> = {
    python: 'solution.py',
    javascript: 'solution.js',
    java: 'Solution.java',
    cpp: 'solution.cpp',
    c: 'solution.c',
    csharp: 'Solution.cs',
    sql: 'solution.sql',
  };
  return map[lang] || 'solution.py';
}

function problemNotesKey(problemId: number): string {
  return `nn_problem_notes_${problemId}`;
}

function renderBackendWait(label: string): React.ReactNode {
  return (
    <div className="nn-wait">
      <span className="nn-spinner" aria-hidden="true" />
      <span className="nn-wait-text">{label}</span>
    </div>
  );
}

type SubmissionItem = {
  id: number;
  attempt_id: number | null;
  problem_id: number;
  language: string;
  status: string;
  error_message?: string | null;
  execution_time: number | null;
  memory_usage: number | null;
  passed_tests: number | null;
  total_tests: number | null;
  code_hash: string | null;
  submitted_at: string;
  code: string;
};

type ClassProblemContext = {
  class_id: number;
  class_name: string;
  lesson_id: number;
  lesson_title: string;
  due_at: string | null;
  comments?: {
    id: number;
    body: string;
    createdAt: string;
    author_role?: string;
    submission_id?: number | null;
  }[];
  feedback: {
    id: number;
    body: string;
    createdAt: string;
    submission_id: number | null;
  } | null;
  submission_id: number | null;
};

function localTodayYmd() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

const ProblemPage = () => {
  const { topic: topicSlug, slug: problemSlug } = useParams<{ topic: string; slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const { currentLanguage, setLanguage: setUiLanguage, t } = useTranslation();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscriptionStatus();
  const { showAchievements } = useAchievementNotifications() ?? {};

  const [problem, setProblem] = useState<Problem | null>(null);
  /** Числовой id задачи после загрузки по SEO-пути (для API submit/attempts). */
  const id = problem?.id != null ? String(problem.id) : undefined;
  const [linkedTheoryLesson, setLinkedTheoryLesson] = useState<LinkedTheoryLesson | null>(null);
  const [classContext, setClassContext] = useState<ClassProblemContext | null>(null);
  const [classReplyDraft, setClassReplyDraft] = useState('');
  const [classReplySending, setClassReplySending] = useState(false);
  const [classReplyError, setClassReplyError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    'description' | 'schema' | 'mySolutions' | 'bookmarks'
  >(() => (new URLSearchParams(window.location.search).get('openSubmission') ? 'mySolutions' : 'description'));
  /** После редиректа на /404 не показываем fallback «задача не найдена» до размонтирования */
  const [redirectingToNotFound, setRedirectingToNotFound] = useState(false);
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [terminalOutput, setTerminalOutput] = useState<React.ReactNode>(null);
  const [submissionPanel, setSubmissionPanel] = useState<SubmissionPanelState | null>(null);
  const [bottomPanelTab, setBottomPanelTab] = useState<BottomPanelTab>('terminal');
  const [selectedTestIndex, setSelectedTestIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const handleRunCodeRef = useRef<() => void>(() => undefined);
  const handleSubmitCodeRef = useRef<() => void>(() => undefined);
  const isSqlSandboxRef = useRef(false);
  const isInteractiveRef = useRef(false);
  const editorActionsDisposeRef = useRef<Array<{ dispose: () => void }>>([]);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const [showEditorSettingsModal, setShowEditorSettingsModal] = useState(false);
  const [interactiveAnswer, setInteractiveAnswer] = useState('');
  const [sqlSyntaxTheme, setSqlSyntaxTheme] = useState<SciplexSqlThemeId>(readStoredSqlSyntaxTheme);
  const [sqlCustomThemeTokens, setSqlCustomThemeTokens] = useState<SqlCustomThemeTokens>(
    readStoredSqlCustomThemeTokens,
  );
  const [codeSyntaxTheme, setCodeSyntaxTheme] = useState<SciplexCodeThemeId>(readStoredCodeSyntaxTheme);
  const [codeCustomThemeTokens, setCodeCustomThemeTokens] = useState<CodeCustomThemeTokens>(
    readStoredCodeCustomThemeTokens,
  );
  const [sqlEditorBackgroundMode, setSqlEditorBackgroundMode] = useState<EditorBackgroundMode>(
    readStoredSqlEditorBackgroundMode,
  );
  const [codeEditorBackgroundMode, setCodeEditorBackgroundMode] = useState<EditorBackgroundMode>(
    readStoredCodeEditorBackgroundMode,
  );
  const [bugDescription, setBugDescription] = useState('');
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugToast, setBugToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  /** Диалект SQL для задач с topic === 'SQL': postgres или mysql */
  const [sqlDialect, setSqlDialect] = useState<'postgres' | 'mysql'>('postgres');
  const [sandboxPresetKey, setSandboxPresetKey] = useState<string>('family');

  const [mySubmissions, setMySubmissions] = useState<SubmissionItem[]>([]);
  const [mySubmissionsLoading, setMySubmissionsLoading] = useState(false);
  const [mySubmissionsError, setMySubmissionsError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionItem | null>(null);
  const [notes, setNotes] = useState('');
  const [bookmarked, setBookmarked] = useState(false);
  const [favoriteBookmarks, setFavoriteBookmarks] = useState<ProblemBookmark[]>([]);
  const [bookmarkToast, setBookmarkToast] = useState<string | null>(null);
  const bookmarkToastTimerRef = useRef<number | null>(null);
  const [linkCopyToast, setLinkCopyToast] = useState(false);
  const linkCopyToastTimerRef = useRef<number | null>(null);
  const [solutionInsertToast, setSolutionInsertToast] = useState<string | null>(null);
  const solutionInsertToastTimerRef = useRef<number | null>(null);
  const [notesPanelVisible, setNotesPanelVisible] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [descPaneWidth, setDescPaneWidth] = useState(DESC_PANE_DEFAULT_WIDTH);
  const [isDescResizing, setIsDescResizing] = useState(false);
  const [descResizeEnabled, setDescResizeEnabled] = useState(
    () => typeof window !== 'undefined' && window.innerWidth > 1200
  );
  const descResizeStartXRef = useRef(0);
  const descResizeStartWidthRef = useRef(DESC_PANE_DEFAULT_WIDTH);
  const [sideLangMenuOpen, setSideLangMenuOpen] = useState(false);
  const sideLangMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(sideLangMenuRef, () => setSideLangMenuOpen(false));

  useEffect(() => {
    const onResize = () => setDescResizeEnabled(window.innerWidth > 1200);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleDescResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    descResizeStartXRef.current = event.clientX;
    descResizeStartWidthRef.current = descPaneWidth;
    setIsDescResizing(true);
  }, [descPaneWidth]);

  useEffect(() => {
    if (!isDescResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      const delta = event.clientX - descResizeStartXRef.current;
      const nextWidth = clampDescPaneWidth(descResizeStartWidthRef.current + delta);
      setDescPaneWidth(nextWidth);
    };

    const handleMouseUp = () => {
      setIsDescResizing(false);
      setDescPaneWidth((current) => clampDescPaneWidth(current));
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDescResizing]);

  const workspaceGridStyle = useMemo(() => {
    if (!descResizeEnabled) return undefined;
    return {
      gridTemplateColumns: notesPanelVisible
        ? `${descPaneWidth}px minmax(280px, 1fr) minmax(220px, 260px)`
        : `${descPaneWidth}px minmax(280px, 1fr)`,
    } as React.CSSProperties;
  }, [descPaneWidth, descResizeEnabled, notesPanelVisible]);

  const sideLanguageLabel: Record<string, string> = { ru: 'RU', en: 'EN' };
  const sideLanguages = ['ru', 'en'] as const;

  // -----------------------------
  // Attempt/session telemetry (aggregated, no raw keystrokes)
  // -----------------------------
  const attemptIdRef = useRef<number | null>(null);
  const clientSessionIdRef = useRef<string>(
    `cs_${Date.now().toString(16)}_${Math.random().toString(16).slice(2)}`
  );
  const attemptStartTsRef = useRef<number>(0);
  const hiddenSinceRef = useRef<number | null>(null);
  const lastEditTsRef = useRef<number>(0);
  const lastBurstTsRef = useRef<number>(0);
  const firstEditorInteractionSentRef = useRef<boolean>(false);
  const firstRunSentRef = useRef<boolean>(false);
  const firstSubmitSentRef = useRef<boolean>(false);
  const lastBigPasteAtRef = useRef<number | null>(null);
  const formatAfterPasteWindowUntilRef = useRef<number>(0);

  const metricsAccRef = useRef({
    // increments
    focus_blur_count: 0,
    visibility_change_count: 0,
    tab_hidden_time_ms: 0,
    active_edit_time_ms: 0,
    run_count: 0,
    submit_count: 0,
    executor_error_count: 0,
    wrong_answer_count: 0,
    insert_ops: 0,
    delete_ops: 0,
    replace_ops: 0,
    chars_inserted_total: 0,
    chars_deleted_total: 0,
    edit_bursts_count: 0,
    undo_count: 0,
    redo_count: 0,
    paste_ops: 0,
    paste_chars_total: 0,
    big_paste_count: 0,
    post_paste_edit_chars: 0,
    cursor_move_events: 0,
    selection_events: 0,
    format_count: 0,
    // maxima / last known
    code_len_last: 0,
    largest_single_insert_chars: 0,
    largest_single_delete_chars: 0,
    // big paste times (relative ms)
    first_big_paste_ms: null as number | null,
    last_big_paste_ms: null as number | null,
    // format flag (best-effort)
    auto_format_used: null as boolean | null,
  });
  const telemetryFlushTimerRef = useRef<number | null>(null);

  const flushTelemetry = async (extra?: Record<string, unknown>) => {
    const attemptId = attemptIdRef.current;
    if (!attemptId || !isAuthenticated) return;

    const acc = metricsAccRef.current;
    const payload: Record<string, unknown> = {
      ...extra,
      language: problem?.topic === 'SQL' ? 'sql' : language,
      run_count: acc.run_count,
      submit_count: acc.submit_count,
      executor_error_count: acc.executor_error_count,
      wrong_answer_count: acc.wrong_answer_count,
      focus_blur_count: acc.focus_blur_count,
      visibility_change_count: acc.visibility_change_count,
      tab_hidden_time_ms: acc.tab_hidden_time_ms,
      active_edit_time_ms: acc.active_edit_time_ms,
      insert_ops: acc.insert_ops,
      delete_ops: acc.delete_ops,
      replace_ops: acc.replace_ops,
      chars_inserted_total: acc.chars_inserted_total,
      chars_deleted_total: acc.chars_deleted_total,
      edit_bursts_count: acc.edit_bursts_count,
      undo_count: acc.undo_count,
      redo_count: acc.redo_count,
      paste_ops: acc.paste_ops,
      paste_chars_total: acc.paste_chars_total,
      big_paste_count: acc.big_paste_count,
      first_big_paste_ms: acc.first_big_paste_ms,
      last_big_paste_ms: acc.last_big_paste_ms,
      post_paste_edit_chars: acc.post_paste_edit_chars,
      cursor_move_events: acc.cursor_move_events,
      selection_events: acc.selection_events,
      format_count: acc.format_count,
      auto_format_used: acc.auto_format_used,
      code_len_last: acc.code_len_last,
      largest_single_insert_chars: acc.largest_single_insert_chars,
      largest_single_delete_chars: acc.largest_single_delete_chars,
    };

    // Reset incremental counters (best effort); keep maxima + last length.
    metricsAccRef.current = {
      ...metricsAccRef.current,
      focus_blur_count: 0,
      visibility_change_count: 0,
      tab_hidden_time_ms: 0,
      active_edit_time_ms: 0,
      run_count: 0,
      submit_count: 0,
      executor_error_count: 0,
      wrong_answer_count: 0,
      insert_ops: 0,
      delete_ops: 0,
      replace_ops: 0,
      chars_inserted_total: 0,
      chars_deleted_total: 0,
      edit_bursts_count: 0,
      undo_count: 0,
      redo_count: 0,
      paste_ops: 0,
      paste_chars_total: 0,
      big_paste_count: 0,
      post_paste_edit_chars: 0,
      cursor_move_events: 0,
      selection_events: 0,
      format_count: 0,
    };

    try {
      await fetch(`${API_BASE}/api/attempts/${attemptId}/metrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
    } catch {
      // telemetry must not break UX
    }
  };

  const scheduleTelemetryFlush = () => {
    if (telemetryFlushTimerRef.current != null) return;
    telemetryFlushTimerRef.current = window.setTimeout(() => {
      telemetryFlushTimerRef.current = null;
      void flushTelemetry();
    }, 10_000);
  };

  const startAttemptIfNeeded = async (problemIdNum: number, starterCodeLen: number) => {
    if (!isAuthenticated) return;
    if (attemptIdRef.current != null) return;
    if (!Number.isFinite(problemIdNum) || problemIdNum <= 0) return;

    attemptStartTsRef.current = Date.now();
    hiddenSinceRef.current = document.visibilityState === 'hidden' ? Date.now() : null;
    lastEditTsRef.current = 0;
    lastBurstTsRef.current = 0;
    firstEditorInteractionSentRef.current = false;
    firstRunSentRef.current = false;
    firstSubmitSentRef.current = false;

    metricsAccRef.current.code_len_last = starterCodeLen;
    metricsAccRef.current.largest_single_insert_chars = 0;
    metricsAccRef.current.largest_single_delete_chars = 0;
    metricsAccRef.current.first_big_paste_ms = null;
    metricsAccRef.current.last_big_paste_ms = null;
    metricsAccRef.current.auto_format_used = null;
    lastBigPasteAtRef.current = null;
    formatAfterPasteWindowUntilRef.current = 0;

    try {
      const resp = await fetch(`${API_BASE}/api/attempts/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          problem_id: problemIdNum,
          language: problem?.topic === 'SQL' ? 'sql' : language,
          topic: problem?.topic ?? null,
          difficulty: problem?.difficulty ?? null,
          client_session_id: clientSessionIdRef.current,
          code_len_initial: starterCodeLen,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.attempt_id != null) {
        attemptIdRef.current = Number(data.attempt_id);
      }
    } catch {
      // ignore
    }
  };

  const getMonacoLanguage = (lang: string): string => {
    const languageMap: Record<string, string> = {
      'javascript': 'javascript',
      'python': 'python',
      'sql': 'sql'
    };
    return languageMap[lang] || 'python';
  };

  const getStarterCodeFromData = (starterCode: Problem['starter_code'], lang: string): string => {
    if (lang === 'sql' && starterCode?.sql) return starterCode.sql;
    const languageMap: Record<string, keyof Problem['starter_code']> = {
      'python': 'python',
      'javascript': 'javascript',
      'sql': 'sql'
    };
    const mappedLang = languageMap[lang] || 'python';
    return (starterCode as Record<string, string>)[mappedLang] || getDefaultStarterCode(lang);
  };

  const getDefaultStarterCode = (lang: string): string => {
    switch(lang) {
      case 'python':
        return `class Solution:
    def solve(self):
        # Write your solution here
        pass`;
      case 'javascript':
        return `// Write your solution here
function solve() {
    // Your code here
}`;
      case 'sql':
        return '-- Write your PostgreSQL query here\nSELECT 1;';
      default:
        return `class Solution:
    def solve(self):
        # Write your solution here
        pass`;
    }
  };

  const handleBeforeMount = (monaco: any) => {
    defineEditorThemes(
      monaco,
      sqlCustomThemeTokens,
      codeCustomThemeTokens,
      sqlEditorBackgroundMode,
      codeEditorBackgroundMode,
    );
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    setupAutoCompletion(monaco);

    // Telemetry hooks (best effort; aggregated only)
    try {
      if (typeof editor.onDidPaste === 'function') {
        editor.onDidPaste((e: any) => {
          // Monaco doesn't reliably provide pasted text; we count large insert changes as fallback too.
          metricsAccRef.current.paste_ops += 1;
          formatAfterPasteWindowUntilRef.current = Date.now() + 1200;
          scheduleTelemetryFlush();
        });
      }

      const model = editor.getModel?.();
      if (model && typeof model.onDidChangeContent === 'function') {
        model.onDidChangeContent((ev: any) => {
          const now = Date.now();
          const currentValue = model.getValue?.() || '';
          const codeLen = typeof currentValue === 'string' ? currentValue.length : 0;
          metricsAccRef.current.code_len_last = codeLen;

          if (!firstEditorInteractionSentRef.current && attemptStartTsRef.current > 0) {
            firstEditorInteractionSentRef.current = true;
            void flushTelemetry({ first_editor_interaction_ms: Math.max(0, now - attemptStartTsRef.current) });
          }

          // Approx active edit time: count gaps up to 5s
          const prev = lastEditTsRef.current;
          if (prev > 0) {
            const dt = now - prev;
            if (dt > 0 && dt <= 5000) {
              metricsAccRef.current.active_edit_time_ms += dt;
            }
          }
          lastEditTsRef.current = now;

          // Burst counter
          if (lastBurstTsRef.current === 0 || now - lastBurstTsRef.current > 2000) {
            metricsAccRef.current.edit_bursts_count += 1;
          }
          lastBurstTsRef.current = now;

          const changes = Array.isArray(ev?.changes) ? ev.changes : [];
          const attemptStart = attemptStartTsRef.current || 0;
          const BIG_PASTE_THRESHOLD = 200;
          for (const ch of changes) {
            const inserted = typeof ch?.text === 'string' ? ch.text.length : 0;
            const deleted = typeof ch?.rangeLength === 'number' ? Math.max(0, ch.rangeLength) : 0;

            if (inserted > 0 && deleted > 0) metricsAccRef.current.replace_ops += 1;
            else if (inserted > 0) metricsAccRef.current.insert_ops += 1;
            else if (deleted > 0) metricsAccRef.current.delete_ops += 1;

            if (inserted > 0) metricsAccRef.current.chars_inserted_total += inserted;
            if (deleted > 0) metricsAccRef.current.chars_deleted_total += deleted;

            metricsAccRef.current.largest_single_insert_chars = Math.max(
              metricsAccRef.current.largest_single_insert_chars,
              inserted
            );
            metricsAccRef.current.largest_single_delete_chars = Math.max(
              metricsAccRef.current.largest_single_delete_chars,
              deleted
            );

            // Paste heuristic: very large single insert
            if (inserted >= 60) {
              metricsAccRef.current.paste_ops += 1;
              metricsAccRef.current.paste_chars_total += inserted;
            }

            // Big paste: capture timing and reset post-paste counter
            if (attemptStart > 0 && inserted >= BIG_PASTE_THRESHOLD) {
              const ms = Math.max(0, now - attemptStart);
              if (metricsAccRef.current.first_big_paste_ms == null) metricsAccRef.current.first_big_paste_ms = ms;
              metricsAccRef.current.last_big_paste_ms = ms;
              metricsAccRef.current.big_paste_count += 1;
              lastBigPasteAtRef.current = now;
              metricsAccRef.current.post_paste_edit_chars = 0;
              formatAfterPasteWindowUntilRef.current = now + 1200;
            } else if (lastBigPasteAtRef.current != null) {
              metricsAccRef.current.post_paste_edit_chars += inserted + deleted;
            }
          }

          // Best-effort auto-format flag: many small changes soon after paste
          if (
            metricsAccRef.current.auto_format_used !== true &&
            formatAfterPasteWindowUntilRef.current > 0 &&
            now <= formatAfterPasteWindowUntilRef.current &&
            changes.length >= 2
          ) {
            metricsAccRef.current.auto_format_used = true;
          }

          // Very weak heuristic for "format-like" operation
          if (changes.length >= 3) {
            metricsAccRef.current.format_count += 1;
          }

          scheduleTelemetryFlush();
        });
      }

      if (typeof editor.onDidChangeCursorPosition === 'function') {
        editor.onDidChangeCursorPosition(() => {
          metricsAccRef.current.cursor_move_events += 1;
          scheduleTelemetryFlush();
        });
      }
      if (typeof editor.onDidChangeCursorSelection === 'function') {
        editor.onDidChangeCursorSelection(() => {
          metricsAccRef.current.selection_events += 1;
          scheduleTelemetryFlush();
        });
      }
    } catch {
      // ignore
    }

    editorActionsDisposeRef.current.forEach((d) => d.dispose());
    editorActionsDisposeRef.current = [];

    const runAction = editor.addAction({
      id: 'problem-page-run-code',
      label: t('problemPage.runCode'),
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Quote],
      run: () => handleRunCodeRef.current(),
    });
    const submitAction = editor.addAction({
      id: 'problem-page-submit-code',
      label: t('problemPage.submit'),
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
      run: () => {
        if (!isSqlSandboxRef.current) handleSubmitCodeRef.current();
      },
    });
    editorActionsDisposeRef.current.push(runAction, submitAction);

    editor.focus();
  };

  const setupAutoCompletion = (monaco: any) => {
    const languageId = getMonacoLanguage(language);
    
    monaco.languages.registerCompletionItemProvider(languageId, {
      provideCompletionItems: (model: any, position: any) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };

        const suggestions = getLanguageSuggestions(language);
        
        return {
          suggestions: suggestions.map(suggestion => ({
            label: suggestion.label,
            kind: monaco.languages.CompletionItemKind[suggestion.kind],
            insertText: suggestion.insertText,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range: range,
            documentation: suggestion.documentation
          }))
        };
      }
    });
  };

  const getLanguageSuggestions = (lang: string) => {
    const commonSuggestions = [
      { label: 'if', kind: 'Keyword', insertText: 'if (${1:condition}) {\n\t$0\n}', documentation: 'If statement' },
      { label: 'for', kind: 'Keyword', insertText: 'for (let i = 0; i < ${1:count}; i++) {\n\t$0\n}', documentation: 'For loop' },
      { label: 'while', kind: 'Keyword', insertText: 'while (${1:condition}) {\n\t$0\n}', documentation: 'While loop' },
    ];

    switch(lang) {
      case 'javascript':
        return [
          ...commonSuggestions,
          { label: 'console.log', kind: 'Function', insertText: 'console.log(${1:value})', documentation: 'Log to console' },
          { label: 'function', kind: 'Function', insertText: 'function ${1:name}(${2:params}) {\n\t$0\n}', documentation: 'Function declaration' },
          { label: 'const', kind: 'Keyword', insertText: 'const ${1:name} = ${2:value}', documentation: 'Constant declaration' },
          { label: 'let', kind: 'Keyword', insertText: 'let ${1:name} = ${2:value}', documentation: 'Variable declaration' },
          { label: 'return', kind: 'Keyword', insertText: 'return ${1:value}', documentation: 'Return value' },
          { label: 'Array', kind: 'Class', insertText: 'Array', documentation: 'Array object' },
          { label: 'Object', kind: 'Class', insertText: 'Object', documentation: 'Object' },
          { label: 'String', kind: 'Class', insertText: 'String', documentation: 'String object' },
          { label: 'Math', kind: 'Class', insertText: 'Math', documentation: 'Math object' },
        ];
      case 'python':
        return [
          ...commonSuggestions,
          { label: 'print', kind: 'Function', insertText: 'print(${1:value})', documentation: 'Print to console' },
          { label: 'def', kind: 'Keyword', insertText: 'def ${1:name}(${2:params}):\n\t$0', documentation: 'Function definition' },
          { label: 'class', kind: 'Keyword', insertText: 'class ${1:Name}:\n\t$0', documentation: 'Class definition' },
          { label: 'return', kind: 'Keyword', insertText: 'return ${1:value}', documentation: 'Return value' },
          { label: 'import', kind: 'Keyword', insertText: 'import ${1:module}', documentation: 'Import module' },
          { label: 'from', kind: 'Keyword', insertText: 'from ${1:module} import ${2:name}', documentation: 'Import from module' },
          { label: 'len', kind: 'Function', insertText: 'len(${1:object})', documentation: 'Get length' },
          { label: 'range', kind: 'Function', insertText: 'range(${1:start}, ${2:end})', documentation: 'Generate range' },
        ];
      case 'sql':
        return [
          { label: 'SELECT', kind: 'Keyword', insertText: 'SELECT ${1:*}\nFROM ${2:table}\nWHERE ${3:condition};', documentation: 'SELECT query' },
          { label: 'FROM', kind: 'Keyword', insertText: 'FROM ${1:table}', documentation: 'FROM clause' },
          { label: 'WHERE', kind: 'Keyword', insertText: 'WHERE ${1:condition}', documentation: 'WHERE clause' },
          { label: 'JOIN', kind: 'Keyword', insertText: 'JOIN ${1:table} ON ${2:condition}', documentation: 'JOIN clause' },
          { label: 'LEFT JOIN', kind: 'Keyword', insertText: 'LEFT JOIN ${1:table} ON ${2:condition}', documentation: 'LEFT JOIN' },
          { label: 'GROUP BY', kind: 'Keyword', insertText: 'GROUP BY ${1:column}', documentation: 'GROUP BY clause' },
          { label: 'ORDER BY', kind: 'Keyword', insertText: 'ORDER BY ${1:column} ${2:ASC}', documentation: 'ORDER BY clause' },
          { label: 'HAVING', kind: 'Keyword', insertText: 'HAVING ${1:condition}', documentation: 'HAVING clause' },
          { label: 'COUNT', kind: 'Function', insertText: 'COUNT(${1:column})', documentation: 'Count rows' },
          { label: 'SUM', kind: 'Function', insertText: 'SUM(${1:column})', documentation: 'Sum values' },
          { label: 'AVG', kind: 'Function', insertText: 'AVG(${1:column})', documentation: 'Average' },
          { label: 'MAX', kind: 'Function', insertText: 'MAX(${1:column})', documentation: 'Maximum' },
          { label: 'MIN', kind: 'Function', insertText: 'MIN(${1:column})', documentation: 'Minimum' },
          { label: 'DISTINCT', kind: 'Keyword', insertText: 'DISTINCT ${1:column}', documentation: 'Unique values' },
          { label: 'NULL', kind: 'Keyword', insertText: 'NULL', documentation: 'NULL value' },
        ];
      default:
        return commonSuggestions;
    }
  };

  useEffect(() => {
    const fetchProblem = async () => {
      setLoading(true);
      setRedirectingToNotFound(false);
      setProblem(null);
      if (!topicSlug || !problemSlug) {
        setRedirectingToNotFound(true);
        navigate('/404', { replace: true });
        return;
      }
      try {
        const lang = currentLanguage || 'en';
        const response = await fetch(
          `${API_BASE}/api/problems/by-path/${encodeURIComponent(topicSlug)}/${encodeURIComponent(problemSlug)}?lang=${encodeURIComponent(lang)}`,
          { credentials: 'include' }
        );
        if (!response.ok) {
          if (response.status === 404) {
            setRedirectingToNotFound(true);
            navigate('/404', { replace: true });
            return;
          }
          setError(t('problemPage.problemNotFound'));
          return;
        }
        const data = await response.json();
        if (data.canonical_path && data.topic_slug_mismatch) {
          navigate(data.canonical_path, { replace: true });
          return;
        }
        setProblem(data);

        const isSql = data.topic === 'SQL';
        const isSandbox = !!data.is_sql_sandbox;
        const presets: SqlSandboxPreset[] = Array.isArray(data.sql_sandbox_presets)
          ? data.sql_sandbox_presets
          : [];
        const initialPresetKey = isSandbox && presets.length > 0
          ? readSandboxPresetChoice(data.id, presets)
          : 'family';
        if (isSandbox && presets.length > 0) {
          setSandboxPresetKey(initialPresetKey);
        }
        const editorLang = isSql ? 'sql' : language;
        const activePreset = presets.find((preset) => preset.key === initialPresetKey) || presets[0];
        const starterCode = isSandbox && activePreset
          ? getSandboxStarterCode(activePreset, 'postgres')
          : data.starter_code
            ? (isSql && data.starter_code.sql
              ? data.starter_code.sql
              : getStarterCodeFromData(data.starter_code, editorLang))
            : getDefaultStarterCode(editorLang);
        const initialCode = resolveEditorCodeFromStorage(
          data,
          editorLang,
          sqlDialect,
          starterCode,
          isSandbox ? initialPresetKey : undefined
        );
        setCode(initialCode);
        void startAttemptIfNeeded(Number(data.id), (initialCode || '').length);
        if (isSql) {
          setLanguage('sql');
          setSqlDialect('postgres');
          setActiveTab('description');
        }
      } catch (err) {
        setError(t('problemPage.unknownError'));
      } finally {
        setLoading(false);
      }
    };

    fetchProblem();
  }, [topicSlug, problemSlug, currentLanguage, t, navigate]);

  useEffect(() => {
    setInteractiveAnswer('');
  }, [topicSlug, problemSlug]);

  const seoTitle = problem
    ? currentLanguage === 'ru'
      ? `${problem.title} — задача${problem.topic ? ` по ${topicSeoLabelRu(problem.topic)}` : ''} | Sciplex`
      : `${problem.title} — ${problem.topic || 'coding'} problem | Sciplex`
    : 'Sciplex';
  const seoDescription = problem
    ? (problem.description || '')
        .replace(/[#*_`>\-\[\]()]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160) || defaultSiteDescription(currentLanguage)
    : defaultSiteDescription(currentLanguage);
  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    canonicalPath: problem ? problemPath(problem) : undefined,
  });

  useEffect(() => {
    if (!id) {
      setLinkedTheoryLesson(null);
      return;
    }

    let cancelled = false;

    const fetchLinkedTheory = async () => {
      try {
        const lang = currentLanguage || 'en';
        const response = await fetch(
          `${API_BASE}/api/theory/problems/${id}/lesson?lang=${encodeURIComponent(lang)}`,
          { credentials: 'include' }
        );

        if (cancelled) return;

        if (!response.ok) {
          setLinkedTheoryLesson(null);
          return;
        }

        const data = (await response.json()) as { lesson?: LinkedTheoryLesson };
        setLinkedTheoryLesson(data.lesson ?? null);
      } catch {
        if (!cancelled) {
          setLinkedTheoryLesson(null);
        }
      }
    };

    fetchLinkedTheory();

    return () => {
      cancelled = true;
    };
  }, [id, currentLanguage]);

  // Visibility + focus metrics (lightweight, aggregated)
  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisibility = () => {
      metricsAccRef.current.visibility_change_count += 1;
      const now = Date.now();
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = now;
      } else {
        const since = hiddenSinceRef.current;
        if (since != null) {
          metricsAccRef.current.tab_hidden_time_ms += Math.max(0, now - since);
          hiddenSinceRef.current = null;
        }
      }
      scheduleTelemetryFlush();
    };

    const onBlur = () => {
      metricsAccRef.current.focus_blur_count += 1;
      scheduleTelemetryFlush();
    };
    const onFocus = () => {
      metricsAccRef.current.focus_blur_count += 1;
      scheduleTelemetryFlush();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated]);

  // Flush/end attempt on unmount
  useEffect(() => {
    return () => {
      const attemptId = attemptIdRef.current;
      if (!attemptId) return;
      void flushTelemetry();
      void fetch(`${API_BASE}/api/attempts/${attemptId}/end`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
      attemptIdRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (problem?.premium && !hasActiveSubscription && !subscriptionLoading) {
      setShowPremiumModal(true);
    }
  }, [problem?.premium, hasActiveSubscription, subscriptionLoading]);

  useEffect(() => {
    if (!problem?.id) return;
    try {
      const savedNotes = localStorage.getItem(problemNotesKey(problem.id));
      if (savedNotes != null) setNotes(savedNotes);
      migrateLegacyBookmarkIfNeeded({ id: problem.id, title: problem.title });
      setBookmarked(isProblemBookmarked(problem.id));
    } catch {
      // ignore
    }
  }, [problem?.id, problem?.title]);

  useEffect(() => {
    if (activeTab === 'bookmarks') {
      setFavoriteBookmarks(readProblemBookmarks());
    }
  }, [activeTab]);

  useEffect(() => {
    if (!problem?.id) return;
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(problemNotesKey(problem.id), notes);
      } catch {
        // ignore
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [notes, problem?.id]);

  useEffect(() => {
    if (problem) {
      const isSandbox = !!problem.is_sql_sandbox;
      const presets = problem.sql_sandbox_presets || [];
      const activePreset = presets.find((preset) => preset.key === sandboxPresetKey) || presets[0];
      const starterCode = isSandbox && activePreset
        ? getSandboxStarterCode(activePreset, sqlDialect)
        : problem.starter_code
          ? getStarterCodeFromData(problem.starter_code, language)
          : getDefaultStarterCode(language);
      setCode(resolveEditorCodeFromStorage(
        problem,
        language,
        sqlDialect,
        starterCode,
        isSandbox ? sandboxPresetKey : undefined
      ));

      if (monacoRef.current) {
        setupAutoCompletion(monacoRef.current);
        applyEditorTheme(problem.topic === 'SQL');
      }
    }
  }, [language, problem, sqlDialect, sandboxPresetKey]);

  // Автосохранение черновика (localStorage), чтобы пережить перезагрузку страницы
  useEffect(() => {
    if (!problem?.id) return;
    const key = problemDraftStorageKey(
      problem.id,
      language,
      sqlDialect,
      problem.is_sql_sandbox ? sandboxPresetKey : undefined
    );
    const t = window.setTimeout(() => {
      writeProblemDraft(key, code);
    }, 400);
    return () => window.clearTimeout(t);
  }, [code, problem?.id, problem?.is_sql_sandbox, language, sqlDialect, sandboxPresetKey]);

  useEffect(() => {
    const run = async () => {
      if (!isAuthenticated) return;
      if (activeTab !== 'mySolutions') return;
      const pid = Number(id);
      if (!Number.isFinite(pid) || pid <= 0) return;
      setMySubmissionsLoading(true);
      setMySubmissionsError(null);
      try {
        const r = await fetch(`${API_BASE}/api/submissions/problem/${pid}`, { credentials: 'include' });
        if (r.status === 401) {
          setMySubmissionsError(t('problemPage.authRequired'));
          setMySubmissions([]);
          return;
        }
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          setMySubmissionsError(data?.error || t('problemPage.unknownError'));
          setMySubmissions([]);
          return;
        }
        setMySubmissions(Array.isArray(data?.submissions) ? data.submissions : []);
      } catch {
        setMySubmissionsError(t('problemPage.unknownError'));
        setMySubmissions([]);
      } finally {
        setMySubmissionsLoading(false);
      }
    };
    void run();
  }, [activeTab, isAuthenticated, id, t]);

  const openSubmissionParam = searchParams.get('openSubmission');

  useEffect(() => {
    if (openSubmissionParam) setActiveTab('mySolutions');
  }, [openSubmissionParam]);

  useEffect(() => {
    if (!openSubmissionParam || mySubmissionsLoading) return;
    const sid = Number(openSubmissionParam);
    if (!Number.isInteger(sid) || sid <= 0) return;
    const found = mySubmissions.find((item) => item.id === sid);
    if (!found) return;
    if (!id || String(found.problem_id) !== String(id)) return;
    setSelectedSubmission(found);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('openSubmission');
      return next;
    }, { replace: true });
  }, [openSubmissionParam, mySubmissions, mySubmissionsLoading, setSearchParams, id]);

  const classQuery = searchParams.get('class');
  const lessonQuery = searchParams.get('lesson');

  useEffect(() => {
    if (!isAuthenticated || !id) {
      setClassContext(null);
      return;
    }
    const params = new URLSearchParams({
      problemId: String(id),
      today: localTodayYmd(),
    });
    if (classQuery) params.set('classId', classQuery);
    if (lessonQuery) params.set('lessonId', lessonQuery);
    let cancelled = false;
    void fetch(`${API_BASE}/api/classes/problem-context?${params}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { context: null }))
      .then((data) => {
        if (cancelled) return;
        setClassContext(data?.context || null);
        setClassReplyDraft('');
        setClassReplyError(null);
      })
      .catch(() => {
        if (!cancelled) setClassContext(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, id, classQuery, lessonQuery]);

  const classAttemptBody = () => {
    const class_id = Number(classContext?.class_id || classQuery);
    const lesson_id = Number(classContext?.lesson_id || lessonQuery);
    if (!Number.isInteger(class_id) || class_id <= 0 || !Number.isInteger(lesson_id) || lesson_id <= 0) {
      return {};
    }
    return { class_id, lesson_id };
  };

  const openClassSubmission = (submissionId: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('openSubmission', String(submissionId));
      return next;
    });
  };

  const sendClassReply = async () => {
    if (!classContext || classReplySending) return;
    const body = classReplyDraft.trim();
    if (!body) return;
    const teacherNote = (classContext.comments || []).filter((item) => item.author_role !== 'student').slice(-1)[0];
    const submissionId = Number(
      teacherNote?.submission_id || classContext.feedback?.submission_id || classContext.submission_id,
    );
    if (!Number.isInteger(submissionId) || submissionId <= 0) return;
    setClassReplySending(true);
    setClassReplyError(null);
    try {
      const res = await fetch(`${API_BASE}/api/submissions/${submissionId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setClassReplyError(data?.error || t('problemPage.classContextReplyError'));
        return;
      }
      if (data?.comment) {
        setClassContext((prev) => prev
          ? { ...prev, comments: [...(prev.comments || []), data.comment] }
          : prev);
      }
      setClassReplyDraft('');
    } catch {
      setClassReplyError(t('problemPage.classContextReplyError'));
    } finally {
      setClassReplySending(false);
    }
  };

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
  };

  const handleResetCode = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (problem?.id != null) {
      clearProblemDraft(problemDraftStorageKey(
        problem.id,
        language,
        sqlDialect,
        problem.is_sql_sandbox ? sandboxPresetKey : undefined
      ));
    }
    if (problem?.is_sql_sandbox) {
      const preset =
        problem.sql_sandbox_presets?.find((item) => item.key === sandboxPresetKey)
        || problem.sql_sandbox_presets?.[0];
      if (preset) {
        setCode(getSandboxStarterCode(preset, sqlDialect));
        return;
      }
    }
    if (problem?.starter_code) {
      const starterCode = getStarterCodeFromData(problem.starter_code, language);
      setCode(starterCode);
    } else {
      setCode(getDefaultStarterCode(language));
    }
  };

  const handleOpenEditorSettings = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (subscriptionLoading) return;
    if (!hasActiveSubscription) {
      setShowPremiumModal(true);
      return;
    }
    setShowEditorSettingsModal(true);
  };

  const isSqlProblem = problem?.topic === 'SQL';
  const isSqlSandbox = !!problem?.is_sql_sandbox;
  const isInteractive = !!problem?.is_interactive;
  const interactiveConfig = problem?.interactive_config || {};
  const interactiveImageUrl = typeof interactiveConfig.image_url === 'string'
    ? interactiveConfig.image_url
    : '';
  const interactiveDownloadName = typeof interactiveConfig.download_filename === 'string'
    ? interactiveConfig.download_filename
    : 'challenge.png';
  const sandboxPresets = problem?.sql_sandbox_presets || [];
  const activeSandboxPreset =
    sandboxPresets.find((preset) => preset.key === sandboxPresetKey) || sandboxPresets[0] || null;

  const handleSandboxPresetChange = (nextPresetKey: string) => {
    if (!problem?.id) return;
    setSandboxPresetKey(nextPresetKey);
    writeSandboxPresetChoice(problem.id, nextPresetKey);
  };

  useEffect(() => {
    isSqlSandboxRef.current = isSqlSandbox;
  }, [isSqlSandbox]);

  useEffect(() => {
    isInteractiveRef.current = isInteractive;
  }, [isInteractive]);

  const applyEditorTheme = (
    isSql: boolean,
    sqlOverride?: SciplexSqlThemeId,
    codeOverride?: SciplexCodeThemeId,
  ) => {
    const themeName = resolveEditorTheme(
      isSql,
      sqlOverride ?? sqlSyntaxTheme,
      codeOverride ?? codeSyntaxTheme,
    );
    monacoRef.current?.editor?.setTheme?.(themeName);
  };

  const handleSyntaxThemeChange = (
    theme: SciplexSqlThemeId | SciplexCodeThemeId,
    customTokens?: SqlCustomThemeTokens | CodeCustomThemeTokens,
    backgroundMode?: EditorBackgroundMode,
  ) => {
    if (isSqlProblem) {
      const nextCustomTokens = (customTokens as SqlCustomThemeTokens | undefined) ?? sqlCustomThemeTokens;
      const nextBackgroundMode = backgroundMode ?? sqlEditorBackgroundMode;
      setSqlCustomThemeTokens(nextCustomTokens);
      writeStoredSqlCustomThemeTokens(nextCustomTokens);
      setSqlSyntaxTheme(theme as SciplexSqlThemeId);
      writeStoredSqlSyntaxTheme(theme as SciplexSqlThemeId);
      setSqlEditorBackgroundMode(nextBackgroundMode);
      writeStoredSqlEditorBackgroundMode(nextBackgroundMode);

      if (isAuthenticated) {
        void saveEditorSettings({
          sqlSyntaxTheme: theme as SciplexSqlThemeId,
          sqlCustomThemeTokens: nextCustomTokens,
          sqlEditorBackgroundMode: nextBackgroundMode,
        });
      }

      if (monacoRef.current) {
        defineEditorThemes(
          monacoRef.current,
          nextCustomTokens,
          codeCustomThemeTokens,
          nextBackgroundMode,
          codeEditorBackgroundMode,
        );
        applyEditorTheme(true, theme as SciplexSqlThemeId);
      }
      return;
    }

    const nextCustomTokens = (customTokens as CodeCustomThemeTokens | undefined) ?? codeCustomThemeTokens;
    const nextBackgroundMode = backgroundMode ?? codeEditorBackgroundMode;
    setCodeCustomThemeTokens(nextCustomTokens);
    writeStoredCodeCustomThemeTokens(nextCustomTokens);
    setCodeSyntaxTheme(theme as SciplexCodeThemeId);
    writeStoredCodeSyntaxTheme(theme as SciplexCodeThemeId);
    setCodeEditorBackgroundMode(nextBackgroundMode);
    writeStoredCodeEditorBackgroundMode(nextBackgroundMode);

    if (isAuthenticated) {
      void saveEditorSettings({
        codeSyntaxTheme: theme as SciplexCodeThemeId,
        codeCustomThemeTokens: nextCustomTokens,
        codeEditorBackgroundMode: nextBackgroundMode,
      });
    }

    if (monacoRef.current) {
      defineEditorThemes(
        monacoRef.current,
        sqlCustomThemeTokens,
        nextCustomTokens,
        sqlEditorBackgroundMode,
        nextBackgroundMode,
      );
      applyEditorTheme(false, undefined, theme as SciplexCodeThemeId);
    }
  };

  const activeEditorTheme = resolveEditorTheme(isSqlProblem, sqlSyntaxTheme, codeSyntaxTheme);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    let cancelled = false;
    void (async () => {
      const settings = await fetchEditorSettings();
      if (cancelled || !settings) return;

      if (settings.sqlSyntaxTheme) {
        setSqlSyntaxTheme(settings.sqlSyntaxTheme);
        writeStoredSqlSyntaxTheme(settings.sqlSyntaxTheme);
      }

      setSqlCustomThemeTokens(settings.sqlCustomThemeTokens);
      writeStoredSqlCustomThemeTokens(settings.sqlCustomThemeTokens);

      if (settings.codeSyntaxTheme) {
        setCodeSyntaxTheme(settings.codeSyntaxTheme);
        writeStoredCodeSyntaxTheme(settings.codeSyntaxTheme);
      }

      setCodeCustomThemeTokens(settings.codeCustomThemeTokens);
      writeStoredCodeCustomThemeTokens(settings.codeCustomThemeTokens);

      if (settings.sqlEditorBackgroundMode) {
        setSqlEditorBackgroundMode(settings.sqlEditorBackgroundMode);
        writeStoredSqlEditorBackgroundMode(settings.sqlEditorBackgroundMode);
      }

      if (settings.codeEditorBackgroundMode) {
        setCodeEditorBackgroundMode(settings.codeEditorBackgroundMode);
        writeStoredCodeEditorBackgroundMode(settings.codeEditorBackgroundMode);
      }

      if (monacoRef.current) {
        defineEditorThemes(
          monacoRef.current,
          settings.sqlCustomThemeTokens,
          settings.codeCustomThemeTokens,
          settings.sqlEditorBackgroundMode ?? sqlEditorBackgroundMode,
          settings.codeEditorBackgroundMode ?? codeEditorBackgroundMode,
        );
        const isSql = problem?.topic === 'SQL';
        monacoRef.current.editor?.setTheme?.(
          resolveEditorTheme(isSql, settings.sqlSyntaxTheme ?? undefined, settings.codeSyntaxTheme ?? undefined),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authLoading, problem?.topic]);

  const showSolutionInsertToast = (message: string) => {
    if (solutionInsertToastTimerRef.current != null) {
      window.clearTimeout(solutionInsertToastTimerRef.current);
    }
    setSolutionInsertToast(message);
    solutionInsertToastTimerRef.current = window.setTimeout(() => {
      setSolutionInsertToast(null);
      solutionInsertToastTimerRef.current = null;
    }, 2200);
  };

  const handleInsertSolutionCode = () => {
    if (!problem) return;
    const solution = getSolutionCodeFromProblem(problem, language, sqlDialect);
    if (!solution) {
      showSolutionInsertToast(t('problemPage.insertSolutionMissing'));
      return;
    }
    setCode(solution);
    editorRef.current?.setValue?.(solution);
    showSolutionInsertToast(t('problemPage.insertSolutionDone'));
  };

  const isAdminUser = user?.role === 'admin';

  const getDifficultyColor = () => {
    if (!problem) return '#000';
    switch (problem.difficulty) {
      case 'Easy': return 'var(--nn-difficulty-easy)';
      case 'Medium': return 'var(--nn-difficulty-medium)';
      case 'Hard': return 'var(--nn-difficulty-hard)';
      default: return '#000';
    }
  };

  const showBookmarkToast = (message: string) => {
    if (bookmarkToastTimerRef.current != null) {
      window.clearTimeout(bookmarkToastTimerRef.current);
    }
    setBookmarkToast(message);
    bookmarkToastTimerRef.current = window.setTimeout(() => {
      setBookmarkToast(null);
      bookmarkToastTimerRef.current = null;
    }, 2200);
  };

  const toggleBookmark = () => {
    if (!problem?.id) return;
    const added = toggleProblemBookmark({ id: problem.id, title: problem.title });
    setBookmarked(added);
    setFavoriteBookmarks(readProblemBookmarks());
    showBookmarkToast(
      added ? t('problemPage.bookmarkAdded') : t('problemPage.bookmarkRemoved')
    );
  };

  const handleRemoveFavorite = (problemId: number) => {
    removeProblemBookmark(problemId);
    const next = readProblemBookmarks();
    setFavoriteBookmarks(next);
    if (problem?.id === problemId) {
      setBookmarked(false);
    }
  };

  const openBugReport = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setShowBugModal(true);
  };

  const copyProblemLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      if (linkCopyToastTimerRef.current != null) {
        window.clearTimeout(linkCopyToastTimerRef.current);
      }
      setLinkCopyToast(true);
      linkCopyToastTimerRef.current = window.setTimeout(() => {
        setLinkCopyToast(false);
        linkCopyToastTimerRef.current = null;
      }, 2200);
    } catch {
      // ignore
    }
  };

  const toggleNotebookPanel = () => {
    setNotesPanelVisible((open) => {
      const next = !open;
      if (next) {
        window.setTimeout(() => {
          document.querySelector('.problem-notes-input')?.focus();
        }, 50);
      }
      return next;
    });
  };

  const saveSubmissionToDB = async (submissionData: any) => {
    try {
      if (!user) {
        console.warn('User not authenticated, skipping submission save');
        return;
      }

      const status = normalizeSubmissionStatus(submissionData);
      const error_message = buildSubmissionErrorMessage(submissionData, status);

      const submissionPayload = {
        attempt_id: attemptIdRef.current,
        problem_id: parseInt(id || '0'),
        language: problem?.topic === 'SQL' ? sqlDialect : language,
        code,
        status,
        error_message,
        execution_time: submissionData.execution_time || 0,
        memory_usage: submissionData.memory_usage || 0,
        passed_tests: submissionData.passed_tests ?? 0,
        total_tests: submissionData.total_tests ?? 0,
        ...classAttemptBody(),
      };

      const saveResponse = await fetch(`${API_BASE}/api/submissions/save-submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(submissionPayload),
      });

      if (!saveResponse.ok) {
        console.warn('Failed to save submission to DB');
        return;
      }

      const saveData = await saveResponse.json().catch(() => ({}));
      const earned = saveData?.earned_achievements;
      if (Array.isArray(earned) && earned.length > 0 && typeof showAchievements === 'function') {
        showAchievements(earned);
      }
    } catch (error) {
      console.error('Error saving submission:', error);
    }
  };

  const handleRunCode = async () => {
    if (isInteractive) return;

    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!code.trim()) {
      setTerminalOutput(t('problemPage.pleaseWriteCode'));
      setBottomPanelTab('terminal');
      return;
    }

    setIsRunning(true);
    setBottomPanelTab('terminal');
    setTerminalOutput(renderBackendWait(t('problemPage.running')));
    metricsAccRef.current.run_count += 1;
    if (!firstRunSentRef.current && attemptStartTsRef.current > 0) {
      firstRunSentRef.current = true;
      void flushTelemetry({ first_run_ms: Math.max(0, Date.now() - attemptStartTsRef.current) });
    } else {
      scheduleTelemetryFlush();
    }

    try {
      if (problem?.topic === 'SQL') {
        const tc = problem.test_cases?.[0];
        const schemaSql = isSqlSandbox && activeSandboxPreset
          ? getSandboxSchemaSql(activeSandboxPreset, sqlDialect)
          : sqlDialect === 'mysql'
            ? (tc?.schema_sql_mysql ?? tc?.schema_sql ?? '')
            : (tc?.schema_sql ?? '');
        const seedSql = sqlDialect === 'mysql' ? (tc?.seed_sql_mysql ?? tc?.seed_sql ?? '') : (tc?.seed_sql ?? '');
        const response = await fetch(`${EXECUTOR_BASE}/execute_sql`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: code.trim(),
            schema_sql: schemaSql,
            seed_sql: seedSql,
            read_only: isSqlSandbox ? true : problem?.sql_read_only !== false,
            use_prepared: isSqlSandbox,
            prepared_target: isSqlSandbox ? sandboxPresetKey : undefined,
            dialect: sqlDialect,
          }),
        });
        const parsed = await parseJsonResponse<{
          error?: string;
          message?: string;
          result?: unknown;
          columns?: string[];
        }>(response);
        if (!parsed.ok) {
          setTerminalOutput(
            <span style={{ color: 'var(--nn-error)' }}>
              {`${t('problemPage.failedToExecute')}${parsed.error}`}
            </span>
          );
          setIsRunning(false);
          return;
        }
        const result = parsed.data;
        if (!response.ok || result.error) {
          setTerminalOutput(
            <span style={{ color: 'var(--nn-warning)' }}>
              {`${t('problemPage.errorPrefix')} ${result.error || result.message || t('problemPage.executionFailed')}`}
            </span>
          );
        } else {
          setTerminalOutput(
            renderSqlRunResult(
              result.result,
              t('problemPage.codeExecutedSuccess'),
              Array.isArray(result.columns) ? result.columns : undefined
            )
          );
        }
        setIsRunning(false);
        return;
      }

      const inputPayload = problem?.test_cases?.[0]?.input != null
        ? JSON.stringify(problem.test_cases[0].input)
        : '';

      const response = await fetch(`${EXECUTOR_BASE}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language,
          code,
          input: inputPayload,
          problem_id: problem?.id ?? id ?? '',
        }),
      });

      if (response.status === 401) {
        logout();
        setShowAuthModal(true);
        setIsRunning(false);
        return;
      }

      const parsed = await parseJsonResponse<{
        error?: string;
        output?: string;
      }>(response);
      if (!parsed.ok) {
        setTerminalOutput(
          <span style={{ color: 'var(--nn-error)' }}>
            {`${t('problemPage.failedToExecute')}${parsed.error}`}
          </span>
        );
        setIsRunning(false);
        return;
      }
      const result = parsed.data;

      if (!response.ok || result.error) {
        setTerminalOutput(
          <span style={{ color: 'var(--nn-warning)' }}>
            {`${t('problemPage.errorPrefix')} ${result.error || t('problemPage.executionFailed')}\n${result.output || ''}`}
          </span>
        );
      } else {
        setTerminalOutput(
          renderCodeRunResult(result.output ?? '', t('problemPage.codeExecutedSuccess'))
        );
      }
    } catch (err) {
      setTerminalOutput(
        <span style={{ color: 'var(--nn-error)' }}>
          {`${t('problemPage.failedToExecute')}${err instanceof Error ? err.message : t('problemPage.unknownError')}`}
        </span>
      );
    } finally {
      setIsRunning(false);
    }
  };

  const handleSubmitCode = async () => {
    if (isSqlSandbox) return;

    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (isInteractive) {
      const answer = interactiveAnswer.trim();
      if (!answer) {
        setTerminalOutput(t('problemPage.pleaseEnterAnswer'));
        setBottomPanelTab('terminal');
        return;
      }

      setIsRunning(true);
      setBottomPanelTab('tests');
      setSubmissionPanel(null);
      metricsAccRef.current.submit_count += 1;
      if (!firstSubmitSentRef.current && attemptStartTsRef.current > 0) {
        firstSubmitSentRef.current = true;
        void flushTelemetry({ first_submit_ms: Math.max(0, Date.now() - attemptStartTsRef.current) });
      } else {
        scheduleTelemetryFlush();
      }

      const submitStartedAt = performance.now();
      try {
        const response = await fetch(`${API_BASE}/api/problems/${id}/submit-answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            answer,
            attempt_id: attemptIdRef.current,
            ...classAttemptBody(),
          }),
        });

        if (response.status === 401) {
          logout();
          setShowAuthModal(true);
          setIsRunning(false);
          return;
        }

        const parsed = await parseJsonResponse<SubmissionResponse & { earned_achievements?: unknown[] }>(response);
        if (!parsed.ok) {
          setSubmissionPanel({
            status: 'Error',
            message: `${t('problemPage.failedToSubmit')}${parsed.error}`,
            passedTests: 0,
            totalTests: 1,
            executionTime: Math.round(performance.now() - submitStartedAt),
          });
          setIsRunning(false);
          return;
        }

        const responseData = parsed.data;
        if (responseData.status === 'Wrong Answer') metricsAccRef.current.wrong_answer_count += 1;
        if (responseData.status === 'Error') metricsAccRef.current.executor_error_count += 1;
        if (metricsAccRef.current.submit_count === 1 && responseData.status === 'Accepted') {
          void flushTelemetry({ accepted_on_first_submit: true });
        } else {
          scheduleTelemetryFlush();
        }

        const panel = buildSubmissionPanelState(
          responseData,
          1,
          Math.round(performance.now() - submitStartedAt)
        );
        setSubmissionPanel(panel);

        if (Array.isArray(responseData.earned_achievements) && responseData.earned_achievements.length > 0
          && typeof showAchievements === 'function') {
          showAchievements(responseData.earned_achievements as Parameters<typeof showAchievements>[0]);
        }

        if (responseData.status === 'Accepted') {
          setProblem((prev) => (prev ? { ...prev, status: 'Solved' } : prev));
        } else if (problem?.status !== 'Solved') {
          setProblem((prev) => (prev ? { ...prev, status: 'Attempted' } : prev));
        }
      } catch (err) {
        setSubmissionPanel({
          status: 'Error',
          message: `${t('problemPage.failedToSubmit')}${err instanceof Error ? err.message : t('problemPage.unknownError')}`,
          passedTests: 0,
          totalTests: 1,
          executionTime: Math.round(performance.now() - submitStartedAt),
        });
      } finally {
        setIsRunning(false);
      }
      return;
    }

    if (!code.trim()) {
      setTerminalOutput(t('problemPage.pleaseWriteCode'));
      setBottomPanelTab('terminal');
      return;
    }

    setIsRunning(true);
    setBottomPanelTab('tests');
    setSubmissionPanel(null);
    metricsAccRef.current.submit_count += 1;
    if (!firstSubmitSentRef.current && attemptStartTsRef.current > 0) {
      firstSubmitSentRef.current = true;
      void flushTelemetry({ first_submit_ms: Math.max(0, Date.now() - attemptStartTsRef.current) });
    } else {
      scheduleTelemetryFlush();
    }

    const submitStartedAt = performance.now();
    try {
      if (problem?.topic === 'SQL') {
        const response = await fetch(`${EXECUTOR_BASE}/submit_sql/${id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: code.trim(), dialect: sqlDialect }),
        });
        const parsedSql = await parseJsonResponse<SubmissionResponse>(response);
        if (!parsedSql.ok) {
          setSubmissionPanel({
            status: 'Error',
            message: `${t('problemPage.failedToSubmit')}${parsedSql.error}`,
            passedTests: 0,
            totalTests: problem?.test_cases?.length ?? 0,
            executionTime: Math.round(performance.now() - submitStartedAt),
          });
          setIsRunning(false);
          return;
        }
        const responseData = parsedSql.data;
        const fallbackMs = Math.round(performance.now() - submitStartedAt);
        // Attempt result signals (best effort)
        if (responseData.status === 'Wrong Answer') metricsAccRef.current.wrong_answer_count += 1;
        if (responseData.status === 'Error') metricsAccRef.current.executor_error_count += 1;
        if (metricsAccRef.current.submit_count === 1 && responseData.status === 'Accepted') {
          void flushTelemetry({ accepted_on_first_submit: true });
        } else {
          scheduleTelemetryFlush();
        }
        const sqlTcCount = problem?.test_cases?.length ?? 0;
        const sqlPanel = buildSubmissionPanelState(responseData, sqlTcCount, fallbackMs);
        const sqlStatus = normalizeSubmissionStatus(responseData);
        await saveSubmissionToDB({
          ...responseData,
          language: 'sql',
          status: sqlStatus,
          error_message: buildSubmissionErrorMessage(responseData, sqlStatus),
          passed_tests: sqlPanel.passedTests ?? 0,
          total_tests: sqlPanel.totalTests ?? sqlTcCount,
          execution_time: sqlPanel.executionTime ?? 0,
          memory_usage: sqlPanel.memoryUsage ?? 0,
        });
        setSubmissionPanel(sqlPanel);
        if (responseData.test_case != null) {
          setSelectedTestIndex(publicTestIndexOrZero(responseData.test_case - 1));
        }
        setIsRunning(false);
        return;
      }

      const response = await fetch(`${EXECUTOR_BASE}/submit/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language,
          code,
        }),
      });

      if (response.status === 401) {
        logout();
        setShowAuthModal(true);
        setIsRunning(false);
        return;
      }

      const parsed = await parseJsonResponse<SubmissionResponse>(response);
      if (!parsed.ok) {
        setSubmissionPanel({
          status: 'Error',
          message: `${t('problemPage.failedToSubmit')}${parsed.error}`,
          passedTests: 0,
          totalTests: problem?.test_cases?.length ?? 0,
          executionTime: Math.round(performance.now() - submitStartedAt),
        });
        setBottomPanelTab('tests');
        setIsRunning(false);
        return;
      }
      const responseData = parsed.data;
      const fallbackMs = Math.round(performance.now() - submitStartedAt);
      // Attempt result signals (best effort)
      if (responseData.status === 'Wrong Answer') metricsAccRef.current.wrong_answer_count += 1;
      if (responseData.status === 'Error') metricsAccRef.current.executor_error_count += 1;
      if (metricsAccRef.current.submit_count === 1 && responseData.status === 'Accepted') {
        void flushTelemetry({ accepted_on_first_submit: true });
      } else {
        scheduleTelemetryFlush();
      }

      const tcCount = problem?.test_cases?.length ?? 0;
      const panel = buildSubmissionPanelState(responseData, tcCount, fallbackMs);
      const submitStatus = normalizeSubmissionStatus(responseData);
      await saveSubmissionToDB({
        ...responseData,
        status: submitStatus,
        error_message: buildSubmissionErrorMessage(responseData, submitStatus),
        passed_tests: panel.passedTests ?? 0,
        total_tests: panel.totalTests ?? tcCount,
        execution_time: panel.executionTime ?? 0,
        memory_usage: panel.memoryUsage ?? 0,
      });
      if (
        !response.ok &&
        responseData.status !== 'Wrong Answer' &&
        responseData.status !== 'Accepted'
      ) {
        panel.message =
          responseData.error ?? responseData.message ?? t('problemPage.submissionFailed');
      }
      setSubmissionPanel(panel);
      if (responseData.test_case != null) {
        setSelectedTestIndex(publicTestIndexOrZero(responseData.test_case - 1));
      } else if (responseData.results?.length) {
        const failed = responseData.results.find((r) => !r.passed);
        if (failed) setSelectedTestIndex(publicTestIndexOrZero(failed.test_case - 1));
      }
    } catch (err) {
      setSubmissionPanel({
        status: 'Error',
        message: `${t('problemPage.failedToSubmit')}${err instanceof Error ? err.message : t('problemPage.unknownError')}`,
        passedTests: 0,
        totalTests: problem?.test_cases?.length ?? 0,
        executionTime: Math.round(performance.now() - submitStartedAt),
      });
      setBottomPanelTab('tests');
    } finally {
      setIsRunning(false);
    }
  };

  handleRunCodeRef.current = () => {
    if (isRunning) return;
    void handleRunCode();
  };
  handleSubmitCodeRef.current = () => {
    if (isRunning) return;
    void handleSubmitCode();
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;

      const target = e.target;
      if (target instanceof HTMLElement) {
        if (target.closest('.monaco-editor')) return;
        const tag = target.tagName;
        const isInteractiveAnswerInput = target.classList.contains('interactive-answer-input');
        if ((tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') && !isInteractiveAnswerInput) {
          return;
        }
        if (isInteractiveAnswerInput && e.key === 'Enter') {
          e.preventDefault();
          handleSubmitCodeRef.current();
          return;
        }
      }

      if (showAuthModal || showPremiumModal || showBugModal || showEditorSettingsModal || selectedSubmission) return;

      const isRunKey = e.key === "'" || e.code === 'Quote';
      if (isRunKey) {
        if (isInteractiveRef.current) return;
        e.preventDefault();
        handleRunCodeRef.current();
        return;
      }

      if (e.key === 'Enter') {
        if (isSqlSandboxRef.current) return;
        e.preventDefault();
        handleSubmitCodeRef.current();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showAuthModal, showPremiumModal, showBugModal, showEditorSettingsModal, selectedSubmission, isSqlSandbox, isInteractive]);

  useEffect(
    () => () => {
      editorActionsDisposeRef.current.forEach((d) => d.dispose());
      editorActionsDisposeRef.current = [];
    },
    []
  );

  const runButtonTitle = !isAuthenticated
    ? t('problemPage.runCodeTitleLogin')
    : t('problemPage.runCodeTitle');
  const submitButtonTitle = !isAuthenticated
    ? t('problemPage.submitTitleLogin')
    : t('problemPage.submitTitle');

  const editorSettingsButtonTitle = !isAuthenticated
    ? t('problemPage.editorSettingsTitleLogin')
    : !hasActiveSubscription
      ? t('problemPage.editorSettingsTitlePremium')
      : t('problemPage.editorSettings');

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  useCloseOnEscape(showAuthModal, closeAuthModal);
  useCloseOnEscape(showBugModal, () => setShowBugModal(false));
  useCloseOnEscape(showEditorSettingsModal, () => setShowEditorSettingsModal(false));

  const returnToProblem = () => currentReturnPath(window.location.pathname, window.location.search);

  const goToLogin = () => {
    const from = returnToProblem();
    navigate(authHref('/login', from), { state: { from } });
  };

  const goToRegister = () => {
    const from = returnToProblem();
    navigate(authHref('/register', from), { state: { from } });
  };

  if (loading || authLoading) {
    return <div className="loading">{renderBackendWait(t('problemPage.loading'))}</div>;
  }
  if (redirectingToNotFound) return null;
  if (error) return <div className="error">{t('problemPage.errorPrefix')} {error}</div>;
  if (!problem) return <div>{t('problemPage.problemNotFound')}</div>;
  if (problem.premium && subscriptionLoading) {
    return <div className="loading">{renderBackendWait(t('problemPage.loading'))}</div>;
  }

  const theoryLessonHref = linkedTheoryLesson
    ? linkedTheoryLesson.category
      ? `/theory?category=${encodeURIComponent(linkedTheoryLesson.category.slug)}&lesson=${encodeURIComponent(linkedTheoryLesson.slug)}`
      : `/theory?lessonId=${linkedTheoryLesson.id}`
    : null;
  const sqlTc = isSqlProblem ? problem.test_cases?.[0] : undefined;
  const schemaSql =
    !isSqlProblem
      ? ''
      : isSqlSandbox && activeSandboxPreset
        ? getSandboxSchemaSql(activeSandboxPreset, sqlDialect)
        : (sqlTc?.schema_sql ?? '');
  const sqlExampleOutputRaw: unknown =
    isSqlProblem ? (sqlTc?.output ?? sqlTc?.expected ?? null) : null;
  const sqlExampleOutputParsed: unknown = (() => {
    if (typeof sqlExampleOutputRaw !== 'string') return sqlExampleOutputRaw;
    const raw = sqlExampleOutputRaw.trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return sqlExampleOutputRaw;
    }
  })();

  const isSolved =
    isAuthenticated &&
    ((problem as any)?.status === 'Solved' ||
      (problem as any)?.is_solved === true ||
      (problem as any)?.solved === true);

  const isPremiumBlocked = problem.premium && !hasActiveSubscription;
  if (isPremiumBlocked) {
    return (
      <div className="problem-page">
        <div className="problem-premium-blocked">
          <span className="problem-premium-blocked-icon" aria-hidden>🔒</span>
          <h2>{t('premiumRequired.title')}</h2>
          <p>{t('premiumRequired.message')}</p>
          <button
            type="button"
            className="problem-premium-blocked-btn"
            onClick={() => navigate('/subscription')}
          >
            {t('premiumRequired.goToSubscription')}
          </button>
        </div>
        {showPremiumModal && (
          <PremiumRequiredModal onClose={() => setShowPremiumModal(false)} />
        )}
      </div>
    );
  }

  const solutionFile = getSolutionFileName(problem.topic === 'SQL' ? 'sql' : language);
  const difficultyLabel =
    problem.difficulty === 'Easy'
      ? t('problemPage.difficultyEasy')
      : problem.difficulty === 'Medium'
        ? t('problemPage.difficultyMedium')
        : t('problemPage.difficultyHard');

  return (
    <div className="problem-page">
      {linkCopyToast && (
        <div className="problem-link-toast" role="status" aria-live="polite">
          {t('problemPage.linkCopied')}
        </div>
      )}
      {bookmarkToast && (
        <div className="problem-link-toast" role="status" aria-live="polite">
          {bookmarkToast}
        </div>
      )}
      {solutionInsertToast && (
        <div className="problem-link-toast" role="status" aria-live="polite">
          {solutionInsertToast}
        </div>
      )}
      {bugToast && (
        <div
          className={`problem-bug-toast problem-bug-toast-${bugToast.type}`}
          role="status"
          aria-live="polite"
        >
          {bugToast.message}
        </div>
      )}

      <aside
        className={`problem-icon-rail${railCollapsed ? ' problem-icon-rail--collapsed' : ''}`}
        aria-label={t('problemPage.problemPanelNav')}
      >
        <nav className="problem-rail-nav">
          <button
            type="button"
            className={`problem-rail-item${activeTab === 'description' ? ' is-active' : ''}`}
            title={t('problemPage.railCondition')}
            onClick={() => setActiveTab('description')}
          >
            <span className="problem-rail-icon" aria-hidden>
              <FaClipboardList />
            </span>
            <span className="problem-rail-label">{t('problemPage.railCondition')}</span>
          </button>

          {isSqlProblem && (
            <button
              type="button"
              className={`problem-rail-item${activeTab === 'schema' ? ' is-active' : ''}`}
              title={t('problemPage.tabSchema')}
              onClick={() => setActiveTab('schema')}
            >
              <span className="problem-rail-icon" aria-hidden>
                <FaDatabase />
              </span>
              <span className="problem-rail-label">{t('problemPage.tabSchema')}</span>
            </button>
          )}

          <button
            type="button"
            className={`problem-rail-item${activeTab === 'mySolutions' ? ' is-active' : ''}`}
            title={t('problemPage.railMySolutions')}
            onClick={() => {
              if (!isAuthenticated) setShowAuthModal(true);
              else setActiveTab('mySolutions');
            }}
          >
            <span className="problem-rail-icon" aria-hidden>
              <FaClipboardCheck />
            </span>
            <span className="problem-rail-label">{t('problemPage.railMySolutions')}</span>
          </button>

          {theoryLessonHref && linkedTheoryLesson && (
            <Link
              to={theoryLessonHref}
              className="problem-rail-item"
              title={linkedTheoryLesson.title}
            >
              <span className="problem-rail-icon" aria-hidden>
                <FaBook />
              </span>
              <span className="problem-rail-label">{t('problemPage.railTheory')}</span>
            </Link>
          )}

          <button
            type="button"
            className={`problem-rail-item${activeTab === 'bookmarks' ? ' is-active' : ''}`}
            title={t('problemPage.railBookmarks')}
            onClick={() => setActiveTab('bookmarks')}
          >
            <span className="problem-rail-icon" aria-hidden>
              <FaBookmark />
            </span>
            <span className="problem-rail-label">{t('problemPage.railBookmarks')}</span>
          </button>

          <button
            type="button"
            className={`problem-rail-item${notesPanelVisible ? ' is-active' : ''}`}
            title={t('problemPage.railNotebook')}
            onClick={toggleNotebookPanel}
            aria-pressed={notesPanelVisible}
          >
            <span className="problem-rail-icon" aria-hidden>
              <FaStickyNote />
            </span>
            <span className="problem-rail-label">{t('problemPage.railNotebook')}</span>
          </button>
        </nav>

        <button
          type="button"
          className="problem-rail-collapse"
          onClick={() => setRailCollapsed((c) => !c)}
          title={railCollapsed ? t('problemPage.railExpand') : t('problemPage.railCollapse')}
          aria-expanded={!railCollapsed}
        >
          <FaChevronLeft
            className={`problem-rail-collapse-icon${railCollapsed ? ' is-flipped' : ''}`}
            aria-hidden
          />
        </button>
      </aside>

      <div
        className={`problem-workspace${notesPanelVisible ? ' problem-workspace--with-notes' : ''}`}
        style={workspaceGridStyle}
      >
        <section className="problem-pane problem-pane--desc">
          <div className="problem-desc-toolbar">
            <Link to="/problems" className="problem-back-link">
              <FaArrowLeft size={12} aria-hidden />
              <span>{t('problemPage.backToProblems')}</span>
            </Link>
            <div className="problem-desc-actions">
              <button
                type="button"
                className={`problem-icon-action ${bookmarked ? 'is-active' : ''}`}
                onClick={toggleBookmark}
                title={
                  bookmarked
                    ? t('problemPage.bookmarkRemove')
                    : t('problemPage.bookmarkAdd')
                }
                aria-pressed={bookmarked}
                aria-label={
                  bookmarked
                    ? t('problemPage.bookmarkRemove')
                    : t('problemPage.bookmarkAdd')
                }
              >
                <FaBookmark size={14} />
              </button>
              <button
                type="button"
                className="problem-icon-action"
                onClick={() => void copyProblemLink()}
                title={t('problemPage.copyCondition')}
              >
                <FaLink size={14} />
              </button>
              <button
                type="button"
                className="problem-icon-action"
                onClick={openBugReport}
                title={t('problemPage.reportBug')}
                aria-label={t('problemPage.reportBug')}
              >
                <FaBug size={14} />
              </button>
            </div>
          </div>

          {classContext ? (
            <div className="problem-class-banner">
              <div className="problem-class-banner__row">
                <span className="problem-class-banner__icon" aria-hidden>
                  <FaGraduationCap />
                </span>
                <div className="problem-class-banner__copy">
                  <Link to={`/class/${classContext.class_id}`} className="problem-class-banner__class">
                    {classContext.class_name}
                  </Link>
                  <span className="problem-class-banner__lesson">{classContext.lesson_title}</span>
                  {classContext.due_at ? (
                    <span
                      className={`problem-class-banner__due${classContext.due_at < localTodayYmd() ? ' is-overdue' : ''}`}
                    >
                      {(classContext.due_at < localTodayYmd()
                        ? t('problemPage.classContextOverdue')
                        : t('problemPage.classContextDue')
                      ).replace('{date}', classContext.due_at)}
                    </span>
                  ) : null}
                </div>
                <Link to={`/class/${classContext.class_id}`} className="problem-class-banner__back">
                  {t('problemPage.classContextBack')}
                </Link>
              </div>
              {(() => {
                const thread = (classContext.comments && classContext.comments.length > 0)
                  ? classContext.comments
                  : (classContext.feedback?.body ? [classContext.feedback] : []);
                const canReply = thread.some((item) => item.author_role !== 'student');
                if (thread.length === 0) return null;
                return (
                  <div className="problem-class-banner__comment">
                    <span className="problem-class-banner__icon" aria-hidden>
                      <FaCommentDots />
                    </span>
                    <div className="problem-class-banner__thread">
                      <div className="problem-class-banner__comment-label">
                        {t('problemPage.classContextComment')}
                      </div>
                      <ul className="problem-class-banner__list">
                        {thread.map((item) => (
                          <li
                            key={item.id}
                            className={`problem-class-banner__item is-${item.author_role === 'student' ? 'student' : 'teacher'}`}
                          >
                            <span className="problem-class-banner__item-role">
                              {item.author_role === 'student'
                                ? t('problemPage.classContextYou')
                                : t('problemPage.classContextTeacher')}
                            </span>
                            <p>{item.body}</p>
                          </li>
                        ))}
                      </ul>
                      {(classContext.feedback?.submission_id || classContext.submission_id) ? (
                        <button
                          type="button"
                          className="problem-class-banner__open"
                          onClick={() => openClassSubmission(
                            Number(classContext.feedback?.submission_id || classContext.submission_id),
                          )}
                        >
                          {t('problemPage.classContextOpenSubmission')}
                        </button>
                      ) : null}
                      {canReply ? (
                        <form
                          className="problem-class-banner__reply"
                          onSubmit={(e) => {
                            e.preventDefault();
                            void sendClassReply();
                          }}
                        >
                          <textarea
                            className="problem-class-banner__reply-input"
                            rows={2}
                            maxLength={500}
                            value={classReplyDraft}
                            onChange={(e) => setClassReplyDraft(e.target.value)}
                            placeholder={t('problemPage.classContextReplyPlaceholder')}
                            disabled={classReplySending}
                          />
                          {classReplyError ? (
                            <span className="problem-class-banner__reply-error">{classReplyError}</span>
                          ) : null}
                          <button
                            type="submit"
                            className="problem-class-banner__open"
                            disabled={classReplySending || !classReplyDraft.trim()}
                          >
                            {classReplySending
                              ? t('problemPage.classContextReplySending')
                              : t('problemPage.classContextReply')}
                          </button>
                        </form>
                      ) : null}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : null}

          <header className="problem-title-block">
            <h1 className="problem-title">
              <span className="problem-title-id">{problem.id}.</span> {problem.title}
            </h1>
            <div className="problem-title-meta">
              {problem.premium && (
                <span className="premium-badge premium-badge--gold">{t('problemPage.premium')}</span>
              )}
              {user?.role === 'admin' && problem.admin_only ? (
                <span className="problem-draft-badge-header" title={t('problemsList.draftBadge')}>
                  {t('problemsList.draftBadge')}
                </span>
              ) : null}
              {isSolved && (
                <span className="problem-solved-badge problem-solved-badge--compact" aria-label={t('problemPage.solvedBadge')}>
                  <FaCheck size={12} />
                  <span>{t('problemPage.solvedBadge')}</span>
                </span>
              )}
            </div>
            <div className="problem-tags-row">
              {problem.topic ? <span className="problem-topic-pill">{problem.topic}</span> : null}
              {problem.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
              <span className="problem-difficulty-pill" style={{ color: getDifficultyColor() }}>
                <span className="problem-difficulty-dot" style={{ backgroundColor: getDifficultyColor() }} />
                {difficultyLabel}
              </span>
            </div>
          </header>

          <div className="problem-description">
          {activeTab === 'bookmarks' ? (
            <div className="problem-favorites">
              <h2 className="problem-favorites-title">{t('problemPage.railBookmarks')}</h2>
              {favoriteBookmarks.length === 0 ? (
                <p className="problem-favorites-empty">{t('problemPage.bookmarksEmpty')}</p>
              ) : (
                <ul className="problem-favorites-list">
                  {favoriteBookmarks.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/problems/${item.id}`}
                        className={`problem-favorites-item${
                          item.id === problem.id ? ' is-current' : ''
                        }`}
                        onClick={() => setActiveTab('description')}
                      >
                        <span className="problem-favorites-item-title">
                          <span className="problem-favorites-item-id">{item.id}.</span>{' '}
                          {item.title}
                        </span>
                        <FaBookmark size={14} aria-hidden />
                      </Link>
                      <button
                        type="button"
                        className="problem-favorites-remove"
                        title={t('problemPage.bookmarkRemove')}
                        aria-label={t('problemPage.bookmarkRemove')}
                        onClick={() => handleRemoveFavorite(item.id)}
                      >
                        <FaTimes size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : activeTab === 'mySolutions' ? (
            <div className="problem-my-solutions">
              {mySubmissionsLoading ? (
                <div className="problem-panel-loading">{renderBackendWait(t('problemPage.loading'))}</div>
              ) : mySubmissionsError ? (
                <div className="problem-panel-error">{t('problemPage.errorPrefix')} {mySubmissionsError}</div>
              ) : (
                <SubmissionHistoryList
                  submissions={mySubmissions}
                  locale={currentLanguage}
                  t={t}
                  defaultTestCount={problem?.test_cases?.length ?? 0}
                  onOpenCode={setSelectedSubmission}
                />
              )}
            </div>
          ) : activeTab === 'schema' && isSqlProblem ? (
            <div>
              <div className="problem-schema-diagram">
                <SqlSchemaDiagram
                  key={`${activeSandboxPreset?.key ?? 'default'}-${sqlDialect}`}
                  schemaSql={schemaSql}
                />
              </div>
            </div>
          ) : (
            <>
              <ReactMarkdown
                className="problem-description-markdown"
                remarkPlugins={[remarkGfm]}
                skipHtml
                components={{
                  a: ({ node: _n, children, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                }}
              >
                {problem.description || ''}
              </ReactMarkdown>

              {problem.constraints.length > 0
                && problem.topic !== 'SQL'
                && problem.topic !== 'Специальные' ? (
                <div className="constraints">
                  <h3>{t('problemPage.constraints')}</h3>
                  <ul>
                    {problem.constraints.map((constraint, index) => (
                      <li key={index}>{constraint}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {isSqlSandbox && activeSandboxPreset ? (
                <div className="problem-sandbox-preset-info">
                  <strong>{getSandboxPresetLabel(activeSandboxPreset, currentLanguage)}</strong>
                  {getSandboxPresetDescription(activeSandboxPreset, currentLanguage) ? (
                    <p>{getSandboxPresetDescription(activeSandboxPreset, currentLanguage)}</p>
                  ) : null}
                </div>
              ) : null}

              {problem.examples.length > 0 ? (
              <div className="examples">
                {problem.examples.map((example, index) => {
                  const exampleInput = resolveExampleInput(
                    example.input,
                    problem.test_cases?.[index]?.input,
                    problem.test_cases?.[index],
                    sqlDialect
                  );
                  return (
                  <div key={index} className="example">
                    <h3>{t('problemPage.example')} {index + 1}:</h3>
                    {hasExampleInput(exampleInput) && (
                      <div className="io-block">
                        <strong>{t('problemPage.input')}</strong>{' '}
                        {renderExampleIo(exampleInput)}
                      </div>
                    )}
                    <div className="io-block">
                      <strong>{t('problemPage.output')}</strong>{' '}
                      {isSqlProblem ? (
                        <div style={{ marginTop: 8 }}>
                          {renderSqlRunResult(
                            sqlExampleOutputParsed,
                            '—'
                          )}
                        </div>
                      ) : (
                        renderExampleIo(example.output)
                      )}
                    </div>
                    {example.explanation != null && String(example.explanation).trim() !== '' && (
                      <div className="explanation">
                        <strong>{t('problemPage.explanation')}</strong> {example.explanation}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              ) : null}

            </>
          )}
          </div>
          {descResizeEnabled ? (
            <div
              className={`problem-desc-resize-handle${isDescResizing ? ' is-dragging' : ''}`}
              role="separator"
              aria-orientation="vertical"
              aria-label={t('problemPage.resizeDescPanel')}
              onMouseDown={handleDescResizeStart}
            />
          ) : null}
        </section>

        <section className="problem-pane problem-pane--editor code-editor">
          <div className="editor-file-tabs">
            <div className="editor-file-tabs-left">
              <button type="button" className="editor-file-tab editor-file-tab--active">
                {solutionFile}
              </button>
            </div>
            <div className="editor-file-tabs-actions">
              <div className="problem-side-lang" ref={sideLangMenuRef}>
                <button
                  type="button"
                  className="problem-side-lang-btn"
                  onClick={() => setSideLangMenuOpen((open) => !open)}
                  aria-expanded={sideLangMenuOpen}
                  aria-haspopup="listbox"
                  aria-label="Language"
                >
                  <span className="problem-side-lang-code">
                    {sideLanguageLabel[currentLanguage] || currentLanguage.toUpperCase()}
                  </span>
                  <FaChevronDown
                    size={10}
                    className={`problem-side-lang-chevron${sideLangMenuOpen ? ' is-open' : ''}`}
                    aria-hidden
                  />
                </button>
                <div
                  className={`problem-side-lang-menu${sideLangMenuOpen ? ' is-open' : ''}`}
                  role="listbox"
                  aria-label="Language"
                >
                  {sideLanguages.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      role="option"
                      aria-selected={currentLanguage === lang}
                      className={`problem-side-lang-option${currentLanguage === lang ? ' is-active' : ''}`}
                      onClick={() => {
                        setUiLanguage(lang);
                        setSideLangMenuOpen(false);
                      }}
                    >
                      {sideLanguageLabel[lang]}
                    </button>
                  ))}
                </div>
              </div>
              <Link
                to={isAuthenticated ? '/profile' : authHref('/login', returnToProblem())}
                className="problem-side-profile-link"
                title={isAuthenticated ? t('navigation.profile') : t('navigation.login')}
              >
                {isAuthenticated && user ? (
                  <>
                    <img
                      src={getAvatarSrc(user.avatar)}
                      alt=""
                      className="problem-side-profile-avatar"
                    />
                    <span className="problem-side-profile-name">{user.username}</span>
                  </>
                ) : (
                  <span className="problem-side-profile-name">{t('navigation.login')}</span>
                )}
              </Link>
            </div>
          </div>

          <div className="editor-toolbar">
            <div className="editor-toolbar-left">
              {isInteractive ? (
                <span className="editor-interactive-label">{t('problemPage.interactiveWorkspace')}</span>
              ) : (
                <>
              <select
                className="language-selector"
                value={problem?.topic === 'SQL' ? sqlDialect : language}
                onChange={(e) => {
                  if (problem?.topic === 'SQL') {
                    setSqlDialect(e.target.value as 'postgres' | 'mysql');
                  } else {
                    handleLanguageChange(e.target.value);
                  }
                }}
                aria-label={t('problemPage.editorSettings')}
              >
                {problem?.topic === 'SQL' ? (
                  <>
                    <option value="postgres">PostgreSQL</option>
                    <option value="mysql">MySQL</option>
                  </>
                ) : (
                  <>
                    <option value="python">Python 3.11</option>
                    <option value="javascript">JavaScript</option>
                  </>
                )}
              </select>
              {isSqlSandbox && sandboxPresets.length > 0 ? (
                <select
                  className="language-selector sandbox-preset-selector"
                  value={sandboxPresetKey}
                  onChange={(e) => handleSandboxPresetChange(e.target.value)}
                  aria-label="SQL sandbox dataset"
                >
                  {sandboxPresets.map((preset) => (
                    <option key={preset.key} value={preset.key}>
                      {getSandboxPresetLabel(preset, currentLanguage)}
                    </option>
                  ))}
                </select>
              ) : null}
              <button
                type="button"
                className="editor-toolbar-icon-btn"
                onClick={handleOpenEditorSettings}
                title={editorSettingsButtonTitle}
                aria-label={editorSettingsButtonTitle}
              >
                <FaCog size={14} />
              </button>
              {isAdminUser && (
                <button
                  type="button"
                  className="editor-toolbar-icon-btn editor-toolbar-icon-btn--admin"
                  onClick={handleInsertSolutionCode}
                  title={t('problemPage.insertSolutionTitle')}
                  aria-label={t('problemPage.insertSolutionTitle')}
                >
                  <FaCode size={14} />
                </button>
              )}
              <span className="editor-autosave-label">
                <span className="editor-autosave-dot" aria-hidden />
                {t('problemPage.autosave')}
              </span>
                </>
              )}
            </div>
            <div className="editor-toolbar-actions">
              {!isInteractive ? (
                <button
                  type="button"
                  className={`run-button ${!isAuthenticated ? 'disabled-by-auth' : ''}`}
                  onClick={handleRunCode}
                  disabled={isRunning}
                  title={runButtonTitle}
                >
                  {isRunning && bottomPanelTab === 'terminal'
                    ? t('problemPage.running')
                    : t('problemPage.runCode')}
                </button>
              ) : null}
              {!isSqlSandbox ? (
                <button
                  type="button"
                  className={`submit-button ${!isAuthenticated ? 'disabled-by-auth' : ''}`}
                  onClick={handleSubmitCode}
                  disabled={isRunning}
                  title={isInteractive ? t('problemPage.submitAnswerTitle') : submitButtonTitle}
                >
                  {isRunning && bottomPanelTab === 'tests'
                    ? t('problemPage.submitting')
                    : isInteractive
                      ? t('problemPage.submitAnswer')
                      : t('problemPage.submit')}
                </button>
              ) : null}
            </div>
          </div>

          {isInteractive ? (
            <div className="interactive-workspace">
              {interactiveImageUrl ? (
                <div className="interactive-image-block">
                  <img
                    className="interactive-challenge-image"
                    src={interactiveImageUrl}
                    alt={t('problemPage.interactiveImageAlt')}
                  />
                  <a
                    className="interactive-download-btn"
                    href={interactiveImageUrl}
                    download={interactiveDownloadName}
                  >
                    <FaDownload size={14} aria-hidden />
                    {t('problemPage.downloadImage')}
                  </a>
                </div>
              ) : null}
              <label className="interactive-answer-label" htmlFor="interactive-answer-input">
                {t('problemPage.answerLabel')}
              </label>
              <input
                id="interactive-answer-input"
                className="interactive-answer-input"
                type="text"
                value={interactiveAnswer}
                onChange={(e) => setInteractiveAnswer(e.target.value)}
                placeholder={t('problemPage.answerPlaceholder')}
                autoComplete="off"
                spellCheck={false}
                disabled={isRunning}
              />
              <p className="interactive-answer-hint">{t('problemPage.answerHint')}</p>
            </div>
          ) : (
          <div className="editor-container editor-container-flex">
          <Editor
            height="100%"
            language={getMonacoLanguage(language)}
            value={code}
            onChange={(value) => setCode(value || '')}
            beforeMount={handleBeforeMount}
            onMount={handleEditorDidMount}
            theme={activeEditorTheme}
            options={{
              minimap: { enabled: true },
              fontSize: 14,
              wordWrap: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              formatOnPaste: true,
              formatOnType: true,
              suggestOnTriggerCharacters: true,
              acceptSuggestionOnEnter: 'on',
              tabSize: 2,
              lineNumbers: 'on',
              roundedSelection: false,
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
                useShadows: false
              },
              folding: true,
              lineDecorationsWidth: 5,
              lineNumbersMinChars: 3,
              readOnly: false,
              contextmenu: true,
              matchBrackets: 'always',
              autoClosingBrackets: 'always',
              autoClosingQuotes: 'always',
              autoIndent: 'full',
              bracketPairColorization: {
                enabled: true
              },
            }}
          />
        </div>
          )}

          <ProblemBottomPanel
            activeTab={bottomPanelTab}
            onTabChange={setBottomPanelTab}
            submission={submissionPanel}
            terminalContent={terminalOutput}
            isLoading={isRunning}
            loadingLabel={
              bottomPanelTab === 'tests'
                ? t('problemPage.submitting')
                : t('problemPage.running')
            }
            testCases={problem.test_cases}
            selectedTestIndex={selectedTestIndex}
            onSelectTest={(index) => setSelectedTestIndex(publicTestIndexOrZero(index))}
            t={t}
            emptyPanelHint={
              isInteractive
                ? t('problemPage.panelEmptyHintInteractive')
                : t('problemPage.panelEmptyHint')
            }
            sqlDialect={sqlDialect}
          />

        </section>

        {notesPanelVisible ? (
        <aside className="problem-pane problem-pane--side">
          <div className="problem-side-section">
            <div className="problem-side-head problem-side-head--static">
              <span>{t('problemPage.notesPanel')}</span>
            </div>
            <textarea
              className="problem-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="—"
              rows={5}
            />
          </div>
        </aside>
        ) : null}
      </div>

      {showAuthModal && (
        <div className="auth-modal-overlay">
          <div className="auth-modal">
            <div className="auth-modal-header">
              <h3>{t('problemPage.authRequired')}</h3>
              <button type="button" className="close-modal-btn" onClick={closeAuthModal} aria-label="Close">×</button>
            </div>
            <div className="auth-modal-content">
              <p>{t('problemPage.authModalText1')}</p>
              <p>{t('problemPage.authModalText2')}</p>
              <div className="auth-modal-buttons">
                <button type="button" className="auth-btn login-btn" onClick={goToLogin}>
                  {t('problemPage.authModalLogin')}
                </button>
                <button type="button" className="auth-btn register-btn" onClick={goToRegister}>
                  {t('problemPage.authModalRegister')}
                </button>
              </div>
              <p className="auth-modal-note">
                {t('problemPage.authModalNote')}
              </p>
            </div>
          </div>
        </div>
      )}

      {showPremiumModal && (
        <PremiumRequiredModal onClose={() => setShowPremiumModal(false)} />
      )}

      {showEditorSettingsModal && hasActiveSubscription && (
        <EditorSettingsModal
          isSql={isSqlProblem}
          syntaxTheme={isSqlProblem ? sqlSyntaxTheme : codeSyntaxTheme}
          customThemeTokens={isSqlProblem ? sqlCustomThemeTokens : codeCustomThemeTokens}
          editorBackgroundMode={isSqlProblem ? sqlEditorBackgroundMode : codeEditorBackgroundMode}
          onSyntaxThemeChange={handleSyntaxThemeChange}
          onResetCode={handleResetCode}
          canResetCode={Boolean(problem?.starter_code)}
          t={t}
          onClose={() => setShowEditorSettingsModal(false)}
        />
      )}

      {showBugModal && (
        <div className="bug-modal-overlay" onClick={() => setShowBugModal(false)} role="dialog" aria-modal="true" aria-labelledby="bug-modal-title">
          <div className="bug-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bug-modal-header">
              <h3 id="bug-modal-title">{t('problemPage.reportBugTitle')}</h3>
              <button type="button" className="close-modal-btn" onClick={() => setShowBugModal(false)} aria-label={t('problemPage.close')}>×</button>
            </div>
            <div className="bug-modal-content">
              <label htmlFor="bug-description" className="bug-modal-label">{t('problemPage.bugDescriptionLabel')}</label>
              <textarea
                id="bug-description"
                className="bug-modal-textarea"
                value={bugDescription}
                onChange={(e) => setBugDescription(e.target.value)}
                placeholder={t('problemPage.bugDescriptionPlaceholder')}
                rows={5}
                autoFocus
              />
              <div className="bug-modal-actions">
                <button type="button" className="bug-modal-cancel" onClick={() => setShowBugModal(false)}>
                  {t('problemPage.cancel')}
                </button>
                <button
                  type="button"
                  className="bug-modal-submit"
                  disabled={bugSubmitting || !bugDescription.trim()}
                  onClick={async () => {
                    const desc = bugDescription.trim();
                    if (!desc || bugSubmitting) return;
                    const problemId = problem?.id ?? (id ? Number(id) : null);
                    if (!problemId || !Number.isFinite(problemId)) {
                      setBugToast({ type: 'error', message: t('problemPage.bugErrorNoProblem') });
                      return;
                    }
                    setBugSubmitting(true);
                    try {
                      const r = await fetch(`${API_BASE}/api/problem-bugs`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                          problemId,
                          problemTitle: problem?.title ?? null,
                          description: desc,
                        }),
                      });
                      const data = await r.json().catch(() => ({}));
                      if (r.status === 401) {
                        setShowBugModal(false);
                        const from = returnToProblem();
                        navigate(authHref('/login', from), { state: { from } });
                        return;
                      }
                      if (!r.ok) {
                        setBugToast({ type: 'error', message: data?.message || t('problemPage.bugErrorSend') });
                        return;
                      }
                      setShowBugModal(false);
                      setBugDescription('');
                      setBugToast({ type: 'success', message: t('problemPage.bugSent') });
                      setTimeout(() => setBugToast(null), 4000);
                    } catch {
                      setBugToast({ type: 'error', message: t('problemPage.bugErrorSend') });
                    } finally {
                      setBugSubmitting(false);
                    }
                  }}
                >
                  {bugSubmitting ? t('problemPage.sending') : t('problemPage.send')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSubmission && problem && (
        <SubmissionCodeModal
          submission={selectedSubmission}
          defaultTestCount={problem.test_cases?.length ?? 0}
          locale={currentLanguage}
          t={t}
          onClose={() => setSelectedSubmission(null)}
        />
      )}

    </div>
  );
};

export default ProblemPage;